import React, { useEffect, useMemo, useRef, useState } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, inp, lbl, sel, btnP, btnS } from "../../../lib/styles.js";
import { DOC_TYPES, RATE_CATALOG, STUDIO_ADDRESS, DELIVERABLE_FORMATS, INDUSTRY_LABELS, LEAD_SOURCES, SECTION_DEFS, SESSION_TYPES, SESSION_TYPE_LABELS, SESSION_TYPE_COLORS } from "../../../lib/constants.js";
import { BLANK_QUOTE, BLANK_CONTACT, BLANK_CLIENT, sanitizeParentClientId, fmtMoney, fmtDate, FMT_DATE_DOW, isISODateOnly, getTierLabel, getTierRate, initials, initSections, sortSessions, getJobDateRange, studioPatch, clearLocationPatch } from "../../../lib/utils.js";
import { isProBono, setProBono, proBonoTotalMismatch, PRO_BONO_MISMATCH_TEXT } from "../../../lib/proBono.js";
import { calcLineDiscount, calcTotals, quoteDiscountShownAmounts, resolveVolumePricing, isSectionLocked, isSectionOn, getDeposit, compressImage, isAttachmentTooLarge, isImageAttachment, attachmentTypeFor, MAX_ATTACHMENT_BYTES, confColor, confirmContactMatch, rejectContactMatch, ignoreContactMatch, undoContactMatch, confirmAllContactMatches, allContactMatchesConfirmed, quoteHasLinkedContact, contactNameFromConfirmed, contactNameAfterNew, LINK_CONTACT_MESSAGE, saveAndResolveContactLink, saveAndResolveClientLink, clientJobStats, familyJobStats, suggestClientForContact, primaryContactEmail, hasLidarLineItem, hasEstimatorLineItem, groupLineItems, normalizeLineGroups, quoteLineGroups, lineGroupNotesFrom, resolveGroupLabel, applyLidarInsert, applyEstimatorInsert, cleanGroupLabel, cleanGroupNote, normalizeLidarBlocks, pendingLidarBlocks, lidarBlockName, travelClauseText, turnaroundLines, showsArrayTurnaround, printWordingWarnings } from "../../../lib/builderHelpers.js";
import { uploadAttachment } from "../../../lib/attachmentStorage.js";
import { writeClient, writeContact, deadLinkPatch } from "../../../lib/quoteParty.js";
import { PRINT_WORDING_KEYS, PRINT_WORDING_LABELS, resetWordingPatch, wordingOf } from "../../../lib/printWording.js";
import { writeSchedule, hydrateSessions } from "../../../lib/quoteSchedule.js";
import { callClaude, extractWithAI, applyAIResult, applyAIResultWithNotice } from "../../../lib/ai.js";
import { findSavedRecord } from "../../../lib/quoteBoxSnapshot.js";
import QuoteBoxPanel from "./QuoteBoxPanel.jsx";
import { notesStillMatch, hasDiscountLine, STALE_NOTES_NOTICE } from "../../../lib/aiStartDiscount.js";
import notify from "../../../lib/notify.jsx";
import { resolveAttachmentBytes, aiReadableKind, planAiSend } from "../../../lib/attachmentBytes.js";
import { emailDomain, isOrgDomain } from "../../../lib/identity.js";
import { quoteHoldDates, quoteConflicts, conflictSentences, describeHoldDates, equipmentChipOptions, quoteEquipmentIds, isUnknownEquipmentId, equipmentLabel, normalizeQuoteCrew } from "../../../lib/holds.js";
import QuoteAIInput from "./QuoteAIInput.jsx";
import QuotePdfPreview from "./QuotePdfPreview.jsx";
import LidarEstimator from "./LidarEstimator.jsx";
import DroneEstimator from "./DroneEstimator.jsx";

const ENABLE_AI_INPUT = import.meta.env?.VITE_QUOTE_AI_INPUT === "1";

const CATALOG_GROUPS = [
  ["Sessions", RATE_CATALOG.sessions],
  ["Rig Scanning", RATE_CATALOG.scanning],
  ["Leo Scanning", RATE_CATALOG.leo],
  ["Low Poly Deliverable", RATE_CATALOG.lowpoly],
  ["Objects", RATE_CATALOG.objects],
  ["Environments", RATE_CATALOG.environments],
  ["Add-ons", RATE_CATALOG.addons],
];

// Quote pipeline status (distinct from job statuses) — values per STATUS_COLORS_MAP.
const QUOTE_STATUS_LABELS = { quoted: "Quoted", booked: "Booked", completed: "Completed", lost: "Lost", archived: "Archived" };
// Turnaround presets — BUSINESS DAYS from processing approval (the printed quote already
// reads "business days"; the old "weeks" label was a mismatch). Standard is 3–5 days.
const TURNAROUND_OPTIONS = ["1-2", "3-5", "5-7", "7-10", "10-15", "15-20"];
// Document-section rows in print order. fileFormats is excluded — it lives in the
// Deliverables panel as a spec, not a legal clause. The three always-on legal
// clauses (expiration/nda/payment) lock on; travel/cancellation are smart toggles.
// Mirrors the printed document's order: deliverables clauses first (file formats,
// turnaround), then the terms block. projectTerms/dataDelivery added 2026-08-28.
const SECTION_ORDER = ["fileFormats", "turnaround", "payment", "travel", "cancellation", "projectTerms", "dataDelivery", "expiration", "nda"];

export default function QuoteBuilder({ onSaved, onOpenSaved }) {
  const {
    q,
    setQ,
    savedQuotes = [],
    draftQuotes = [],
    jobs = [],
    contacts = [],
    clients = [],
    saveQuote,
    saveDraft,
    newQuote,
    discardEdits,
    saveContact,
    saveClient,
    crewRoster = [],
    equipmentMap,
    // The lock, the unsaved-edits check and the writers for changes that arrive on
    // their own (lib/quotePersistence.js). Absent in older fixtures.
    quoteBusy = null,
    isQuoteDirty,
    quoteHadRecord,
    updateQuote,
    normalizeOpenQuote,
    setBuilderSaveBlocker,
    onBuilderReplaceStart,
    getQ,
  } = useData();
  const quote = q || BLANK_QUOTE();
  const locked = !!quoteBusy;
  const [showCatalog, setShowCatalog] = useState(false);
  const [showLidar, setShowLidar] = useState(false);
  // Blocks (2026-09-04). The target block new lines land in ("Adding to"), by label;
  // "" = No block. Component state, never persisted: reset when another quote loads,
  // cleared when its block is removed, set to a block the operator just created.
  const [activeGroup, setActiveGroup] = useState("");
  const [showBlockCreator, setShowBlockCreator] = useState(false);
  const [showDrone, setShowDrone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [sessionError, setSessionError] = useState("");
  const [addDayOpen, setAddDayOpen] = useState(false);
  // GO fold A: the live quote, readable synchronously after an await.
  const quoteRef = useRef(null);
  quoteRef.current = quote;
  // AI Start replies apply only while this builder is still open (2026-10-09): closing
  // it (Saved Quotes, another view) bumps the token, so a late reply changes nothing.
  const builderOpenRef = useRef(0);
  useEffect(() => () => { builderOpenRef.current += 1; }, []);
  // The AI box (docs/quotes/ai-box-plan.md). Its session reads the live quote and the CRM
  // through refs, so a reply is always checked against what's on screen now. The session
  // module (and the page renderer it runs) loads the first time the box is used.
  const [boxState, setBoxState] = useState({ phase: "idle" });
  const boxEnvRef = useRef({});
  boxEnvRef.current = { contacts, clients, jobs, crewRoster, equipmentMap };
  const savedListsRef = useRef({ savedQuotes, draftQuotes });
  savedListsRef.current = { savedQuotes, draftQuotes };
  const boxRef = useRef(null);
  const startBox = async (text) => {
    if (!boxRef.current) {
      setBoxState({ phase: "splitting", instruction: text, raw: {} });
      const { createBoxSession } = await import("../../../lib/quoteBox.js");
      if (!boxRef.current) {
        boxRef.current = createBoxSession({
          getQuote: () => getQ?.() || quoteRef.current,
          // Lands only on the quote it was read for; a locked quote defers it and says so.
          commit: (quoteId, update) => (updateQuote ? updateQuote(quoteId, update) : (setQ(update), "applied")),
          callAI: callClaude,
          getEnv: () => boxEnvRef.current,
          getSavedRecord: (id) => findSavedRecord(id, savedListsRef.current.savedQuotes, savedListsRef.current.draftQuotes),
          onChange: setBoxState,
        });
      }
    }
    boxRef.current.start(text);
  };
  // A held preview is about ONE quote. When a switch begins, or another quote loads, or
  // the builder closes, anything open against it is dropped. Keyed on q?.id, not quote.id:
  // BLANK_QUOTE() mints a fresh id every render while q is null.
  useEffect(() => onBuilderReplaceStart?.(() => boxRef.current?.drop()), [onBuilderReplaceStart]);
  useEffect(() => { boxRef.current?.drop(); }, [q?.id]);
  useEffect(() => () => { boxRef.current?.drop(); }, []);
  useEffect(() => { setActiveGroup(""); setShowBlockCreator(false); }, [q?.id]);
  // LiDAR prefill: when extraction read LiDAR blocks out of the brief (quote.lidarPrefill)
  // and at least one block has no LiDAR line on the quote yet, open the estimator
  // pre-filled — once per quote, so closing it stays closed. The AI never priced
  // anything; the calculator does on Insert.
  const lidarAutoOpened = useRef(null);
  useEffect(() => {
    if (!q?.id || lidarAutoOpened.current === q.id) return;
    if (q.lidarDetected && pendingLidarBlocks(q.lidarPrefill, q.lineItems || []).length > 0) {
      lidarAutoOpened.current = q.id;
      setShowLidar(true);
    }
  }, [q?.id, q?.lidarDetected, q?.lidarPrefill, q?.lineItems]);
  // Per-attachment upload state (transient, never persisted): id -> { state, error }.
  const [uploadStates, setUploadStates] = useState({});
  // Files rejected at attach time for exceeding the size cap (transient banner rows).
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  // Holds the original File objects in-session so a failed upload can be retried.
  // Cleared on success/remove; empty after a reload (Retry hides when absent).
  const pendingFilesRef = useRef({});

  const lineItems = quote.lineItems || [];
  const quoteDiscounts = quote.quoteDiscounts || [];
  const sections = quote.sections || initSections();
  // Defensive hydration: pre-multi-day quotes stored sessions without id/type/location.
  const sessionsRaw = hydrateSessions(quote);
  const sortedSessions = sortSessions(sessionsRaw);
  const totals = useMemo(() => calcTotals(lineItems, quoteDiscounts), [lineItems, quoteDiscounts]);
  // The blocks on the quote in print order. A quote that predates the list derives it
  // from its brief + lines; a group typed on a line is appended as a block. Every write
  // below persists this list and the lineGroupNotes derived from it.
  const lineGroups = useMemo(() => quoteLineGroups({ lineGroups: quote.lineGroups, lidarPrefill: quote.lidarPrefill, lineGroupNotes: quote.lineGroupNotes, lineItems: quote.lineItems }),
    [quote.lineGroups, quote.lidarPrefill, quote.lineGroupNotes, quote.lineItems]);
  const activeBlock = lineGroups.find((g) => g.label === activeGroup) || null;
  const lidarPending = useMemo(() => pendingLidarBlocks(quote.lidarPrefill, quote.lineItems), [quote.lidarPrefill, quote.lineItems]);
  // The block the open estimator lands its lines in when the brief has no block left to
  // walk: derived live, so changing "Adding to" re-seeds the estimator's Block and
  // Heading note fields without resetting the rest of its form. Pending brief blocks
  // always win (the walk runs unchanged). Its Insert never touches lidarPrefill.
  const lidarTarget = (showLidar && !lidarPending.length && activeBlock) ? { label: activeBlock.label, note: activeBlock.note } : null;
  // The drone estimator has no brief walk, so the target applies whenever it is open.
  const droneTarget = (showDrone && activeBlock) ? { label: activeBlock.label, note: activeBlock.note } : null;

  // Volume pricing sheet (F5) — one resolver keeps the builder checkbox and the
  // printed PDF in lockstep (see resolveVolumePricing).
  const pricing = resolveVolumePricing(quote);
  const existingRecord = savedQuotes.find(sq => sq.id === quote.id);
  // The contact must be a linked CRM contact, not just a typed name (quoteHasLinkedContact).
  const contactLinked = quoteHasLinkedContact(quote);
  const canSave = quote.clientName?.trim() && quote.contactName?.trim() && contactLinked && lineItems.some(li => li.description?.trim());

  // Holds (2026-09-04, docs/holds/holds-plan.md): the live equipment map (from
  // context, keyed on the studio equipment settings), the quote's effective unit
  // ids, its tentative crew re-keyed against the live roster, the dates it would
  // hold, and the collisions against every other open quote and live job. The
  // conflict check reads only the schedule and the picks, so a keystroke in the
  // client name or the summary never recomputes it.
  const equipmentOptions = useMemo(() => equipmentChipOptions(equipmentMap), [equipmentMap]);
  const effectiveEquipmentIds = quoteEquipmentIds(quote, equipmentMap);
  const unknownEquipmentIds = effectiveEquipmentIds.filter(isUnknownEquipmentId);
  const quoteCrew = normalizeQuoteCrew(quote.crew, crewRoster);
  const crewIds = quoteCrew.map(c => c.id);
  // Everyone on the roster, plus anyone on the quote the roster no longer lists
  // (so a stale pick can still be removed).
  const crewOptions = [
    ...crewRoster.filter(m => m && m.id && m.name),
    ...quoteCrew.filter(c => !crewRoster.some(m => m.id === c.id)),
  ];
  const holdDates = quoteHoldDates(quote);
  const { id: holdQuoteId, jobStatus: holdStatus, holdOff, scanDate: holdScanDate, scanDateTBD: holdScanTBD, sessions: holdSessions, equipmentIds: holdEquipmentIds, equipment: legacyEquipment, selectedEquipment: legacySelectedEquipment, crew: holdCrew } = quote;
  const holdConflicts = useMemo(() => quoteConflicts(
    { id: holdQuoteId, jobStatus: holdStatus, holdOff, scanDate: holdScanDate, scanDateTBD: holdScanTBD, sessions: holdSessions, equipmentIds: holdEquipmentIds, equipment: legacyEquipment, selectedEquipment: legacySelectedEquipment, crew: holdCrew },
    savedQuotes, jobs, crewRoster, { equipmentMap },
  ), [holdQuoteId, holdStatus, holdOff, holdScanDate, holdScanTBD, holdSessions, holdEquipmentIds, legacyEquipment, legacySelectedEquipment, holdCrew, savedQuotes, jobs, crewRoster, equipmentMap]);
  const holdConflictLines = useMemo(() => conflictSentences(holdConflicts, { equipmentMap }), [holdConflicts, equipmentMap]);
  // The written list is authoritative even when empty (quoteEquipmentIds), so a
  // pre-holds quote's legacy names can never resurface after a deselect.
  const toggleEquipment = (id) => set("equipmentIds", effectiveEquipmentIds.includes(id) ? effectiveEquipmentIds.filter(x => x !== id) : [...effectiveEquipmentIds, id]);
  const toggleCrew = (member) => set("crew", crewIds.includes(member.id) ? quoteCrew.filter(c => c.id !== member.id) : [...quoteCrew, { id: member.id, name: member.name }]);

  // Contact picker (F8): the Contact field is a searchable CRM picker. Linking sets
  // BOTH contactName (display + the name-match BLANK_JOB falls back on) AND
  // contactEntityId (the explicit link BLANK_JOB + saveQuote use). Free-typing clears a
  // stale link. "Add new" creates a CRM contact (email optional) and links it. Works
  // independently of the AI ContactMatchPanel and of ENABLE_AI_INPUT (the only contact-
  // linking path in prod today — F12).
  const linkedContact = useMemo(() => (
    quote.contactEntityId ? (contacts.find(c => c.id === quote.contactEntityId) || null) : null
  ), [contacts, quote.contactEntityId]);
  // A link that points at nobody (the record left the CRM) must not stay on the quote:
  // saving or booking would link a phantom. Cleared only once the lists have loaded
  // (lib/quoteParty.js deadLinkPatch), and not as an edit: a quote only looked at
  // stays clean (normalizeOpenQuote).
  useEffect(() => {
    const patch = deadLinkPatch(quote, contacts, clients);
    if (!patch) return;
    if (normalizeOpenQuote) normalizeOpenQuote(patch);
    else setQ(prev => ({ ...prev, ...patch }));
  }, [quote, contacts, clients, normalizeOpenQuote, setQ]);
  // Name, link, email and phone are one person (lib/quoteParty.js writeContact).
  const crmLoaded = contacts.length > 0;
  const pickContact = (c) => setQ(prev => ({ ...prev, ...writeContact(prev, { record: c }) }));
  const typeContact = (text) => setQ(prev => ({ ...prev, ...writeContact(prev, { name: text }, { crmLoaded }) }));
  const unlinkContact = () => setQ(prev => ({ ...prev, ...writeContact(prev, { unlink: true }) }));
  const addAndLinkContact = async ({ name, email, company }) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    // The phone that came in with the inquiry goes onto the new record when this is that
    // person, so linking them can't erase it.
    const inquiryPhone = !quote.contactEntityId && String(quote.contactName || "").trim().toLowerCase() === trimmed.toLowerCase() ? (quote.contactPhone || "") : "";
    const c = { ...BLANK_CONTACT(), name: trimmed, emails: email?.trim() ? [email.trim()] : [], phone: inquiryPhone, company: company?.trim() || "" };
    // F14: link ONLY after the contact actually persists — saveAndResolveContactLink sets
    // contactEntityId on success and leaves it null on failure/no-fn, so the quote never
    // holds a dangling id (chip can't resolve / BLANK_JOB phantom auto-link).
    // The link lands on the quote this was started for: it waits under a lock, and is
    // dropped with a notice if another quote is open by then.
    const quoteId = quote.id;
    const { ok, patch } = await saveAndResolveContactLink(saveContact, c);
    const link = (prev) => ({ ...prev, ...(ok ? writeContact(prev, { record: c }) : patch) });
    const onDropped = () => notify.error(`${trimmed} was added to your contacts after another quote was opened, so the link was not made here. Pick them on the quote it belongs to.`);
    if (updateQuote) updateQuote(quoteId, link, { onDropped }); else setQ(link);
    setMessage(ok ? "" : "Couldn't save the new contact — kept the name as text; try again to link it.");
  };

  /* Client picker — mirror of the Contact picker above, for the ACCOUNT half of
     the CRM. Until 2026-08-26 this field was a bare <input>: no suite-io code
     path had ever created a `clients` doc, so companyEntityId stayed null and
     repeat-client history had nothing to attach to. Same guards as contacts:
     link only on successful save, clear a stale id only once clients have
     provably loaded, keep the link while the CRM list is still empty. */
  const linkedClient = useMemo(() => (
    quote.companyEntityId ? (clients.find(c => c.id === quote.companyEntityId) || null) : null
  ), [clients, quote.companyEntityId]);
  // CD7: typing over a linked name unlinks it AND says so — before, the old id
  // silently stayed attached and the quote + its booked job could point at two
  // different accounts. Transient; cleared by the next pick/link.
  const [clientUnlinkWarn, setClientUnlinkWarn] = useState("");
  const clientsLoaded = clients.length > 0;
  const pickClient = (c) => { setClientUnlinkWarn(""); setQ(prev => ({ ...prev, ...writeClient(prev, { record: c }) })); };
  const typeClient = (text) => {
    if (quote.companyEntityId && clientsLoaded) setClientUnlinkWarn(linkedClient?.name || "");
    setQ(prev => ({ ...prev, ...writeClient(prev, { name: text }, { crmLoaded: clientsLoaded }) }));
  };
  const unlinkClient = () => { setClientUnlinkWarn(""); setQ(prev => ({ ...prev, ...writeClient(prev, { unlink: true }) })); };
  const addAndLinkClient = async ({ name, domain, industry, parentClientId }) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    const c = sanitizeParentClientId({
      ...BLANK_CLIENT(),
      name: trimmed,
      orgDomain: (domain || "").trim(),
      industry: [industry || quote.industry || "other"],
      leadSource: quote.leadSource || "",
      // "Associated studio" — a commercial tie, not ownership. One level,
      // loop-proofed by sanitizeParentClientId.
      parentClientId: parentClientId || "",
    }, clients);
    const quoteId = quote.id;
    const { ok, patch } = await saveAndResolveClientLink(saveClient, c);
    setClientUnlinkWarn("");
    const link = (prev) => ({ ...prev, ...(ok ? writeClient(prev, { record: c }) : patch) });
    const onDropped = () => notify.error(`${trimmed} was added to your clients after another quote was opened, so the link was not made here. Pick them on the quote it belongs to.`);
    if (updateQuote) updateQuote(quoteId, link, { onDropped }); else setQ(link);
    setMessage(ok ? "" : "Couldn't save the new client — kept the name as text; try again to link it.");
  };
  // "2 jobs · last Aug 21" per picker row — computed per jobs change, not per keystroke.
  const jobStatsByClient = useMemo(() => clientJobStats(jobs), [jobs]);
  // Suggest-don't-fill: the linked contact's org domain may identify the account.
  // Rendered as a one-click chip under the field; never auto-applied.
  const suggestedClient = useMemo(() => (
    !quote.companyEntityId && linkedContact ? suggestClientForContact(linkedContact, clients) : null
  ), [quote.companyEntityId, linkedContact, clients]);

  // Hand edits. Refused while the builder is locked (a save, booking, restore or
  // replacement running): the form is inert, and a keyboard path that gets through
  // changes nothing either.
  const set = (field, value) => { if (locked) return; setQ(prev => ({ ...prev, [field]: value })); };
  const setMany = (patch) => { if (locked) return; setQ(prev => ({ ...prev, ...patch })); };
  // Blocks are written as a pair: the list, and the notes map old readers print from.
  const groupsPatch = (groups) => ({ lineGroups: groups, lineGroupNotes: lineGroupNotesFrom(groups) });

  // CRM contact-match review (Step 4 Slice 2). Pure transforms live in
  // builderHelpers. Confirming the rows is optional, except that the quote's
  // contact must be linked: in the Contact picker, or by confirming them here.
  const contactMatches = quote.contactMatches || [];
  const matchesAllConfirmed = allContactMatchesConfirmed(contactMatches);
  // Confirming fills an empty Contact field from the confirmed person.
  const setConfirmedMatches = (next) => setMany({ contactMatches: next, contactName: contactNameFromConfirmed(quote, next) });
  const onConfirmMatch = (i) => setConfirmedMatches(confirmContactMatch(contactMatches, i));
  // "New" confirms the row too (it creates the person on save); the Contact
  // field follows the row (contactNameAfterNew).
  const onRejectMatch = (i) => {
    const next = rejectContactMatch(contactMatches, i);
    setMany({ contactMatches: next, contactName: contactNameAfterNew(quote, contactMatches[i], next) });
  };
  const onIgnoreMatch = (i) => set("contactMatches", ignoreContactMatch(contactMatches, i));
  const onUndoMatch = (i) => set("contactMatches", undoContactMatch(contactMatches, i));
  const onConfirmAllMatches = () => setConfirmedMatches(confirmAllContactMatches(contactMatches));

  const addFromCatalog = (item) => {
    const qty = quote.docType === "rate_sheet" ? null : 1;
    const rate = item.tiered ? getTierRate(item.id, qty || 1) : item.rate;
    set("lineItems", [
      ...lineItems,
      {
        key: `li_${Date.now()}`,
        catalogId: item.id,
        description: item.label,
        notes: item.notes || "",
        qty,
        rate,
        discount: null,
        ...(activeBlock ? { group: activeBlock.label } : {}),
      },
    ]);
    setShowCatalog(false);
  };

  // LiDAR estimator → appends its computed line item(s) and lands the block's heading
  // note on its lineGroups entry; applyLidarInsert (pure, unit-tested) does the work,
  // including the rename write-back for a block renamed before Insert. The widget
  // decides whether to stay open (more blocks pending) or close, so nothing is toggled
  // here. The target pill follows a renamed block.
  const addLidarLineItems = (items, meta = {}) => {
    const label = cleanGroupLabel(meta.label);
    const blocks = normalizeLidarBlocks(quote.lidarPrefill);
    const oldLabel = (blocks && Number.isInteger(meta.blockIndex) && blocks[meta.blockIndex]) ? cleanGroupLabel(blocks[meta.blockIndex].label) : "";
    if (oldLabel && label && oldLabel !== label && activeGroup === oldLabel) setActiveGroup(label);
    setQ((prev) => applyLidarInsert(prev || BLANK_QUOTE(), items, meta));
  };
  // Drone estimator → appends its computed line item(s) and lands the block on the
  // quote's block list the way LiDAR does; applyEstimatorInsert (pure, unit-tested) pins
  // blockIndex to null, so the drone path can never rename a brief block. The widget
  // closes itself after Insert.
  const addDroneLineItems = (items, meta = {}) => {
    setQ((prev) => applyEstimatorInsert(prev || BLANK_QUOTE(), items, meta));
  };
  const setGroupNote = (label, note) => setMany(groupsPatch(lineGroups.map((g) => (g.label === label ? { ...g, note } : g))));
  const clearGroupPrintLabel = (label) => setMany(groupsPatch(lineGroups.map((g) => {
    if (g.label !== label) return g;
    const { printLabel: _shown, ...rest } = g;
    return rest;
  })));
  // New block: appended to the list, empty, and made the target.
  const createGroup = (label, note) => {
    setMany(groupsPatch(normalizeLineGroups([...lineGroups, { label, note }])));
    setActiveGroup(label);
    setShowBlockCreator(false);
  };
  // The x on a heading removes the heading only: its lines move to No block. Never a line.
  const removeGroup = (label) => {
    setMany({
      ...groupsPatch(lineGroups.filter((g) => g.label !== label)),
      lineItems: lineItems.map((li) => (cleanGroupLabel(li.group) === label ? (({ group: _g, ...rest }) => rest)(li) : li)),
    });
    if (activeGroup === label) setActiveGroup("");
  };
  // Suggestions for the creator: brief blocks not on the quote, and each scheduled day
  // (name = the day, note = the place) not already a block.
  const briefSuggestions = (normalizeLidarBlocks(quote.lidarPrefill) || [])
    .map((b) => ({ label: cleanGroupLabel(b.label), note: cleanGroupNote(b.note) }))
    .filter((b) => b.label && !lineGroups.some((g) => g.label === b.label));
  const scheduleSuggestions = sortedSessions
    .filter((sess) => isISODateOnly(sess.date))
    .map((sess) => ({ label: fmtDate(sess.date, FMT_DATE_DOW), note: cleanGroupNote(sess.isStudio ? STUDIO_ADDRESS : sess.location) }))
    .filter((sg, k, all) => !lineGroups.some((g) => g.label === sg.label) && all.findIndex((o) => o.label === sg.label && o.note === sg.note) === k);
  const openLidar = () => setShowLidar(true);
  const closeLidar = () => setShowLidar(false);

  const addCustom = () => set("lineItems", [
    ...lineItems,
    { key: `li_${Date.now()}`, catalogId: null, description: "", notes: "", qty: 1, rate: 0, discount: null, ...(activeBlock ? { group: activeBlock.label } : {}) },
  ]);

  const updateItem = (idx, field, value) => {
    // A typed group means the block it names, case-insensitively ("pile" joins "Pile").
    const v = field === "group" ? resolveGroupLabel(lineGroups, value) : value;
    const next = lineItems.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: v };
      if (field === "qty" && (item.catalogId === "head_scan" || item.catalogId === "body_scan")) {
        const qty = value === "" || value === null ? null : Number(value) || 0;
        if (qty > 0) next.rate = getTierRate(item.catalogId, qty);
      }
      // A rate can never be negative — clamp at the one edit chokepoint so no PDF/total shows
      // a negative (the printable renders the stored rate raw).
      if (field === "rate") {
        const r = Number(value);
        next.rate = Number.isFinite(r) ? Math.max(0, r) : 0;
      }
      return next;
    });
    // A group typed on a line that is not a block yet becomes one (appended).
    if (field === "group") { setMany({ lineItems: next, ...groupsPatch(normalizeLineGroups([...lineGroups, { label: v }])) }); return; }
    set("lineItems", next);
  };

  const removeItem = (idx) => set("lineItems", lineItems.filter((_, i) => i !== idx));

  const toggleLineDiscount = (idx) => {
    set("lineItems", lineItems.map((item, i) => (
      i === idx ? { ...item, discount: item.discount ? null : { name: "", type: "%", value: 0, reason: "" } } : item
    )));
  };

  const updateLineDiscount = (idx, field, value) => {
    set("lineItems", lineItems.map((item, i) => (
      i === idx && item.discount ? { ...item, discount: { ...item.discount, [field]: value } } : item
    )));
  };

  const addQuoteDiscount = () => set("quoteDiscounts", [
    ...quoteDiscounts,
    { key: `qd_${Date.now()}`, name: "", type: "%", value: 0, reason: "" },
  ]);

  const updateQuoteDiscount = (idx, field, value) => {
    set("quoteDiscounts", quoteDiscounts.map((disc, i) => (
      i === idx ? { ...disc, [field]: value } : disc
    )));
  };

  const removeQuoteDiscount = (idx) => set("quoteDiscounts", quoteDiscounts.filter((_, i) => i !== idx));

  // Pro bono (2026-09-11): one switch writes the flag AND the 100% Pro bono
  // discount through the shared helper (src/lib/proBono.js), so the builder, the
  // job card and the AI refine step can never disagree about what pro bono means.
  // The discount stays an ordinary editable row; deleting it by hand while the
  // switch is on shows the mismatch line under the switch instead of re-adding it.
  const toggleProBono = () => setQ(prev => ({ ...prev, ...setProBono(prev, !isProBono(prev)) }));
  const proBonoMismatch = proBonoTotalMismatch(quote, totals.total);

  // Every schedule change goes through the one writer (lib/quoteSchedule.js), so the
  // date, the TBD switch, the sessions, the location and the travel clause stay in step.
  const changeSchedule = (change) => {
    const { patch, refuse } = writeSchedule(quote, change);
    if (refuse) { setSessionError(refuse); return false; }
    setMany(patch);
    setSessionError("");
    return true;
  };
  const handleStudio = (checked) => changeSchedule({ studio: checked });

  const toggleFormat = (formatId) => {
    const current = quote.deliverableFormats || ["obj"];
    const next = current.includes(formatId) ? current.filter(id => id !== formatId) : [...current, formatId];
    set("deliverableFormats", next);
  };

  // ── Multi-day sessions (lib/quoteSchedule.js writeSchedule does the work) ──
  const updateSession = (id, patch) => changeSchedule({ updateSession: { id, patch } });
  const removeSession = (id) => changeSchedule({ removeSession: id });
  const addSession = (sessionType) => { changeSchedule({ addSession: sessionType }); setAddDayOpen(false); };

  const toggleMultiDay = () => {
    if (quote.multiDay) {
      const extras = sessionsRaw.length - 1;
      if (sessionsRaw.length > 1 && !window.confirm(`Turning off multi-day will discard ${extras} session${extras === 1 ? "" : "s"} and keep only the first. Continue?`)) return;
    }
    changeSchedule({ multiDay: !quote.multiDay });
  };

  const sessionSummary = (() => {
    if (!sortedSessions.length) return "";
    const counts = {};
    sortedSessions.forEach(s => { counts[s.sessionType] = (counts[s.sessionType] || 0) + 1; });
    const parts = SESSION_TYPES.filter(t => counts[t]).map(t => `${counts[t]} ${SESSION_TYPE_LABELS[t].toLowerCase()}`);
    return `${sortedSessions.length} session${sortedSessions.length === 1 ? "" : "s"}${parts.length ? " · " + parts.join(", ") : ""}`;
  })();

  const sessionRange = (() => {
    const { start, end } = getJobDateRange({ sessions: sortedSessions, captureDate: quote.scanDate });
    if (!start) return "No dates yet";
    return start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`;
  })();

  const toggleSection = (key) => {
    if (isSectionLocked(quote, key)) return;
    // Seed from the EFFECTIVE state, not the stored entry: a defaultOn section
    // with no stored entry is ON, so its first click must store enabled:false —
    // seeding {enabled:false} here would make that first click a no-op ON write.
    const cur = sections[key] || { enabled: isSectionOn(quote, key), text: SECTION_DEFS[key]?.defaultText || "" };
    set("sections", { ...sections, [key]: { ...cur, enabled: !isSectionOn(quote, key) } });
  };

  const updateSectionText = (key, text) => {
    const cur = sections[key] || { enabled: true, text: "" };
    set("sections", { ...sections, [key]: { ...cur, text } });
  };

  // ── Attachments (reference images + files; durable Firebase Storage upload) ──
  const attachments = Array.isArray(quote.attachments) ? quote.attachments : [];
  // Block saves while any attachment is still uploading, so we never persist a
  // quote with url:null for an image that's about to get a durable Storage url.
  const uploadsPending = Object.values(uploadStates).some(s => s?.state === "uploading");
  // An attachment whose Storage upload FAILED must also block final ingest. Otherwise
  // the quote saves with url:null for that file — it only *looks* attached via the
  // cached IndexedDB dataUrl on this device, and is gone on every other device and on
  // the client PDF (the visible face of K1). Drafts may still save (provisional); the
  // failed card already shows "⚠ Upload failed" + Retry.
  const uploadsFailed = Object.values(uploadStates).some(s => s?.state === "error");
  const patchAttachments = (updater) => {
    if (locked) return;
    setQ(prev => ({ ...prev, attachments: updater(Array.isArray(prev.attachments) ? prev.attachments : []) }));
  };
  // A change to the attachments that arrives on its own (a picture finishing its
  // compression or its upload) lands on the quote it started on (updateQuote): it waits
  // while a save is running, and if another quote is open by then it is dropped and said
  // so, so a file can never print on the wrong quote.
  const patchAttachmentsOf = (quoteId, updater, what, { onApplied, onDropped: cleanup } = {}) => {
    const apply = (prev) => ({ ...prev, attachments: updater(Array.isArray(prev.attachments) ? prev.attachments : []) });
    const onDropped = () => { cleanup?.(); notify.error(`${what} after another quote was opened, so it was not added. Add it again on the quote it belongs to.`); };
    if (updateQuote) updateQuote(quoteId, apply, { onDropped, onApplied });
    else if (quote?.id === quoteId) { setQ(apply); onApplied?.(); }
    else onDropped();
  };

  // Upload one file to Storage, then stamp the durable url + path onto the attachment.
  const uploadOne = async (quoteId, id, file) => {
    setUploadStates(prev => ({ ...prev, [id]: { state: "uploading" } }));
    try {
      const { url, path } = await uploadAttachment(quoteId, id, file);
      patchAttachmentsOf(quoteId, list => list.map(a => (a.id === id ? { ...a, url, storagePath: path } : a)), `${file.name} finished uploading`);
      setUploadStates(prev => { const next = { ...prev }; delete next[id]; return next; });
      delete pendingFilesRef.current[id];
    } catch (e) {
      setUploadStates(prev => ({ ...prev, [id]: { state: "error", error: e?.message || "Upload failed" } }));
    }
  };

  // While an upload runs or has failed the quote can't be saved as an active quote; the
  // saving code asks here (lib/quotePersistence.js getSaveBlocker) so the Unsaved dialog's
  // Save refuses for the same reasons as Ingest.
  useEffect(() => {
    if (!setBuilderSaveBlocker) return undefined;
    setBuilderSaveBlocker(() => (uploadsPending
      ? "Images are still uploading — try again in a moment."
      : uploadsFailed ? "An attachment failed to upload to storage. Retry or remove it before saving — otherwise it won't attach to the quote." : ""));
    return () => setBuilderSaveBlocker(null);
  }, [uploadsPending, uploadsFailed, setBuilderSaveBlocker]);

  const addAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length || locked) return;
    // The whole operation belongs to the quote open now: compression takes a moment, and
    // the builder may have moved to another quote by the time it is done.
    const quoteId = quote.id;
    const tooBig = files.filter(f => isAttachmentTooLarge(f.size));
    const accepted = files.filter(f => !isAttachmentTooLarge(f.size));
    if (tooBig.length) {
      setRejectedFiles(prev => [
        ...prev,
        ...tooBig.map((f, i) => ({ id: `rej_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`, name: f.name, size: f.size })),
      ]);
    }
    // Build attachment records (compress images to a local dataUrl for preview/PDF),
    // append them, then kick off the durable uploads.
    const built = await Promise.all(accepted.map(async (file) => {
      const type = attachmentTypeFor(file);
      const id = `att_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const dataUrl = type === "image" ? await compressImage(file) : null;
      // Images upload the lighter compressed copy (what we display + print), not the
      // original bytes — keeps Firebase storage small. Fall back to the original if
      // compression failed. Non-images upload as-is.
      const uploadFile = (type === "image" && dataUrl) ? dataUrlToFile(dataUrl, file.name) : file;
      return {
        uploadFile,
        attachment: {
          id, name: file.name, type, size: uploadFile.size, url: null,
          aiRead: false, extracted: "", includeInAddendum: type === "image", caption: "",
          ...(dataUrl ? { dataUrl } : {}),
        },
      };
    }));
    built.forEach(({ attachment, uploadFile }) => { pendingFilesRef.current[attachment.id] = uploadFile; });
    // The uploads start only once the pictures are on the quote. If the append is dropped
    // (another quote opened meanwhile), nothing uploads and nothing is left pending.
    patchAttachmentsOf(quoteId, list => [...list, ...built.map(b => b.attachment)], `${built.map(b => b.attachment.name).join(", ")} finished preparing`, {
      onApplied: () => built.forEach(({ attachment, uploadFile }) => uploadOne(quoteId, attachment.id, uploadFile)),
      onDropped: () => built.forEach(({ attachment }) => { delete pendingFilesRef.current[attachment.id]; }),
    });
  };

  // Removing takes the picture off the quote on screen. Its stored file is deleted only
  // by the save that drops it (lib/quotePersistence.js), so Discard brings it back, and a
  // duplicate can never delete the original quote's file.
  const removeAttachment = (id) => {
    patchAttachments(list => list.filter(a => a.id !== id));
    setUploadStates(prev => { const next = { ...prev }; delete next[id]; return next; });
    delete pendingFilesRef.current[id];
  };

  const retryAttachment = (id) => {
    // Re-upload is only possible while the original File is still in hand; after a
    // reload we no longer have it. The card hides Retry once the File is gone.
    const file = pendingFilesRef.current[id];
    if (file && !locked) uploadOne(quote.id, id, file);
  };

  const toggleAttachmentAddendum = (id) =>
    patchAttachments(list => list.map(a => (a.id === id ? { ...a, includeInAddendum: !a.includeInAddendum } : a)));

  const setAttachmentCaption = (id, caption) =>
    patchAttachments(list => list.map(a => (a.id === id ? { ...a, caption } : a)));

  const dismissRejected = (id) => setRejectedFiles(prev => prev.filter(r => r.id !== id));

  // Ingest = commit the quote into the Quotes queue. It lives on the preview
  // screen (the review gate), so this throws on failure and returns the record —
  // QuotePdfPreview owns the success/error UI for it.
  const handleIngestFromPreview = async () => {
    if (uploadsPending) throw new Error("Images are still uploading — try again in a moment.");
    if (uploadsFailed) throw new Error("An attachment failed to upload to storage. Retry or remove it before ingesting — otherwise it won't attach to the quote.");
    if (!canSave) throw new Error(contactLinked ? "Add a client, a contact, and at least one line item first." : LINK_CONTACT_MESSAGE);
    const record = await saveQuote();
    setMessage(`${DOC_TYPES[record.docType]?.label || "Quote"} ingested into Quotes.`);
    // Stay on the preview (it flips to "✓ Ingested") so the operator can still
    // Download the PDF, then closes the overlay themselves. No auto-navigation —
    // that would unmount the overlay the instant ingest resolves.
    return record;
  };

  const handleSaveDraft = async () => {
    if (saving || locked || uploadsPending) return;
    setSaving(true);
    setMessage("");
    try {
      await saveDraft();
      setMessage("Draft saved.");
    } catch (e) {
      setMessage(e?.userMessage || e?.message || "Could not save draft.");
    } finally {
      setSaving(false);
    }
  };

  // New and Discard edits go through the saving module: New stops for the Unsaved
  // dialog when this quote has unsaved edits (or files a draft when it has no record);
  // Discard puts the saved record back on screen.
  const handleNew = async () => {
    const went = await newQuote?.();
    if (went !== false) setMessage("");
  };
  const handleDiscard = async () => {
    if (!discardEdits || locked) return;
    if (!window.confirm("Throw away the edits that aren't saved and show the saved quote again?")) return;
    const went = await discardEdits();
    if (went) setMessage("Edits discarded. The saved quote is back on screen.");
  };
  // Discard edits is for any quote loaded from a stored record: a draft included (a draft
  // saved elsewhere since it was opened has no other way back to its newer version), and
  // one whose row has left the list since (deleted elsewhere: Discard empties the builder).
  const draftRecord = !existingRecord && draftQuotes.find(sq => sq.id === quote.id);
  const dirtySaved = !!((existingRecord || draftRecord || quoteHadRecord?.(quote)) && isQuoteDirty?.(quote));

  // Paste email/notes → Claude extracts a structured quote → applied to state,
  // advancing to the manual builder. Pricing is grounded in historical quotes +
  // this client's past quotes (returning-client detection). Throws on failure so
  // QuoteAIInput can surface the error.
  const extractFromAI = async ({ emailText, notesText, notesTrusted = true }) => {
    const startQuoteId = quote.id;
    const openToken = builderOpenRef.current;
    const stillOpen = () => builderOpenRef.current === openToken;
    // Slice 3: resolve attached PDFs/images to bytes and send them with the text
    // so Claude reads them natively.
    const atts = quote.attachments || [];
    const readable = atts.filter(a => aiReadableKind(a));
    const resolved = (await Promise.all(readable.map(a => resolveAttachmentBytes(a)))).filter(Boolean);
    // planAiSend applies per-file checks (type/size) AND the aggregate budget in
    // order — the same planner the AI screen labels with, so what's promised and
    // what's sent always agree (F1/F2/F4).
    const byId = new Map(resolved.map(r => [r.id, r]));
    const plan = planAiSend(resolved);
    const sendable = plan.filter(p => p.willRead).map(p => byId.get(p.id));
    const skipped = plan.filter(p => !p.willRead).map(p => `${byId.get(p.id).name} (${p.reason})`);
    const failedToRead = readable.length - resolved.length; // resolve returned null
    // Preflight (F2): if there's no text and nothing sendable, stop with the real
    // reason instead of calling Claude blind and surfacing a generic failure.
    const hasText = (emailText && emailText.trim()) || (notesText && notesText.trim());
    if (!hasText && sendable.length === 0 && (skipped.length || failedToRead > 0)) {
      const why = skipped.length ? skipped.join(", ") : "couldn’t be read";
      throw new Error(`The attached file(s) couldn’t be read by the AI (${why}). Add a note, or attach a supported PDF/image under the size limit.`);
    }
    const sentIds = new Set(sendable.map(r => r.id));
    const parsed = await extractWithAI({
      emailText,
      notesText,
      savedQuotes,
      jobs,
      crmRecords: [...(contacts || []), ...(clients || [])],
      attachments: sendable,
    });
    const applyOpts = { contacts: contacts || [], clients: clients || [], notesText, notesTrusted };
    // The quote discount from YOUR NOTES (2026-10-09) comes only from the notes this
    // request read, on the quote it was started for: if the notes were edited or the
    // quote switched while the AI ran, no discount line is applied (no notes = no
    // discount, quote discounts untouched) and the notice says to run it again.
    const sameNotes = (q) => notesStillMatch(q, startQuoteId, notesText, notesTrusted);
    const optsFor = (q) => (sameNotes(q) ? applyOpts : { ...applyOpts, notesText: "" });
    // The one-line notice is read against the current quote (the updater below may run
    // later, and must stay free of side effects).
    const live = quoteRef.current;
    if (!stillOpen()) return; // the builder was closed while the AI ran: apply nothing
    if (live?.id === startQuoteId && live?.step !== "input") {
      // Skipped to the manual builder while the AI ran: keep the manual work.
      setMessage("AI Start finished after you switched to the manual builder, so nothing was applied.");
      return;
    }
    if (live?.id !== startQuoteId) {
      // Another quote is open now: this reply belongs to the one AI Start ran for.
      setMessage("AI Start finished after another quote was opened, so nothing was applied. Open the quote again and rerun AI Start.");
      return;
    }
    const discountNotice = sameNotes(live)
      ? applyAIResultWithNotice(parsed, live, applyOpts).discountNotice
      : (hasDiscountLine(notesText) || hasDiscountLine(live?.notesText) ? STALE_NOTES_NOTICE : "");
    setQ(prev => {
      if (prev?.id !== startQuoteId || prev?.step !== "input" || !stillOpen()) return prev;
      const applied = applyAIResult(parsed, prev, optsFor(prev));
      if (!sentIds.size) return applied;
      // Badge the files the AI actually read.
      return { ...applied, attachments: (applied.attachments || []).map(a => sentIds.has(a.id) ? { ...a, aiRead: true } : a) };
    });
    const notRead = skipped.length + (failedToRead > 0 ? failedToRead : 0);
    const attachmentNote = notRead > 0
      ? `${notRead} attachment${notRead > 1 ? "s" : ""} couldn’t be read by the AI${skipped.length ? ` — ${skipped.join(", ")}` : ""}. They’re still saved with the quote.`
      : "";
    const afterExtract = [discountNotice, attachmentNote].filter(Boolean).join(" ");
    if (afterExtract) setMessage(afterExtract);
  };

  // The lock: while a save, a booking, a restore or a replacement runs, nothing on the
  // builder accepts a click and the message says what is happening (quotePersistence.js).
  const busyOverlay = locked ? (
    <div data-testid="qb-busy" role="status" aria-live="polite" style={S.busyOverlay}>
      <div style={S.busyCard}>
        {quoteBusy.label}…
        {quoteBusy.stuck && <div data-testid="qb-stuck" style={S.busyStuck}>{quoteBusy.message}</div>}
      </div>
    </div>
  ) : null;

  if (ENABLE_AI_INPUT && quote.step === "input") {
    return (
      <div style={{ position: "relative" }}>
        <div style={locked ? S.lockedForm : undefined} inert={locked ? "" : undefined}>
          <QuoteAIInput
            key={quote.id}
            quote={quote}
            set={set}
            saving={saving || locked}
            onExtract={extractFromAI}
            onContinue={() => set("step", "build")}
            onSaveDraft={handleSaveDraft}
            onOpenSaved={onOpenSaved}
          />
        </div>
        {busyOverlay}
      </div>
    );
  }

  return (
    <div className="qb-scope" style={{ fontFamily: font, position: "relative" }}>
      {busyOverlay}
      <style>{`
        .qb-scope input:focus, .qb-scope textarea:focus, .qb-scope select:focus {
          border-color: ${C.text} !important;
          box-shadow: 0 0 0 3px rgba(26,26,26,0.05);
          background: ${C.surface} !important;
        }
        .qb-scope input:hover:not(:focus), .qb-scope textarea:hover:not(:focus), .qb-scope select:hover:not(:focus) {
          border-color: #CFCBC4;
        }
        .qb-scope .qb-panel { transition: box-shadow .18s ease, border-color .18s ease; }
        .qb-scope .qb-panel:hover { box-shadow: 0 1px 3px rgba(26,26,26,0.04); }
        @keyframes dp { 0%,60%,100%{opacity:.25} 30%{opacity:1} }
        @media (max-width: 767px) {
          .qb-scope,
          .qb-scope * {
            min-width: 0;
          }
          .qb-scope .qb-panel {
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
            padding: 16px 14px !important;
            border-radius: 8px !important;
          }
          .quote-builder-line-head,
          .quote-builder-line-row {
            grid-template-columns: minmax(0, 1fr) 44px 56px 28px !important;
            gap: 6px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          .quote-builder-discount-row {
            grid-template-columns: minmax(0, 1fr) auto 28px !important;
            grid-template-areas:
              "name     name   remove"
              "controls amount amount" !important;
            gap: 6px !important;
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          .quote-builder-discount-row > :nth-child(1) { grid-area: name; }
          .quote-builder-discount-row > :nth-child(4) { grid-area: controls; }
          .quote-builder-discount-row > :nth-child(5) { grid-area: amount; text-align: right !important; }
          .quote-builder-discount-row > :nth-child(6) { grid-area: remove; }
          /* Drop the Total cell to fit a phone, but KEEP remove (x) reachable and
             place per-line discount controls on a second row so existing discounts
             can still be edited or cleared on mobile. */
          .quote-builder-line-head > :nth-child(4),
          .quote-builder-line-head > :nth-child(5),
          .quote-builder-line-row > :nth-child(5),
          .quote-builder-discount-row > :nth-child(2),
          .quote-builder-discount-row > :nth-child(3) {
            display: none !important;
          }
          .quote-builder-line-row > :nth-child(6) {
            grid-column: 4;
            grid-row: 1;
          }
          .quote-builder-line-discount {
            grid-column: 1 / -1;
            grid-row: 2;
          }
          /* The per-line head 'Discount' label is hidden on mobile and this
             control reflows to its own row — caption it so it isn't a bare
             value floating under the line item. */
          .quote-builder-line-discount::before {
            content: "Discount";
            display: block;
            font-size: 9px;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: ${C.muted};
            margin-bottom: 3px;
          }
          .quote-builder-line-foot {
            align-items: flex-start !important;
            flex-direction: column !important;
          }
          .quote-builder-foot-totals {
            justify-content: flex-start !important;
          }
          .quote-builder-footer {
            align-items: stretch !important;
          }
          .quote-builder-footer > span {
            flex: 1 0 100%;
            margin-right: 0 !important;
          }
          .quote-builder-footer > button {
            flex: 1 1 auto;
          }
          .quote-builder-session-head {
            display: none !important;
          }
          .quote-builder-session-row {
            grid-template-columns: 18px minmax(0, 1fr) 78px 24px !important;
            grid-template-areas:
              "idx date type remove"
              "idx location studio remove";
            gap: 6px !important;
            padding: 8px 0 !important;
            border-bottom: 1px solid ${C.border};
          }
          .quote-builder-session-row > :nth-child(1) { grid-area: idx; }
          .quote-builder-session-row > :nth-child(2) { grid-area: date; }
          .quote-builder-session-row > :nth-child(3) { grid-area: type; }
          .quote-builder-session-row > :nth-child(4) { grid-area: location; }
          .quote-builder-session-row > :nth-child(5) { grid-area: studio; }
          .quote-builder-session-row > :nth-child(6) { grid-area: remove; }
          .quote-builder-session-card {
            padding: 12px !important;
          }
          .quote-builder-session-headline,
          .quote-builder-contact-head,
          .quote-builder-contact-row,
          .quote-builder-contact-actions,
          .quote-builder-section-head,
          .quote-builder-dropzone,
          .quote-builder-file-sub {
            flex-wrap: wrap !important;
          }
          .quote-builder-contact-row {
            align-items: flex-start !important;
          }
          .quote-builder-contact-verdict {
            text-align: left !important;
            min-width: 0 !important;
            width: 100%;
          }
          .quote-builder-img-grid {
            grid-template-columns: 1fr !important;
          }
          .quote-builder-dropzone {
            text-align: center;
          }
        }
      `}</style>
      <div style={S.header}>
        <div>
          <div style={S.kicker}>{existingRecord ? "Editing Quote" : "Quote Builder"}</div>
          <div style={S.title}>{quote.clientName || "New quote"}</div>
        </div>
        <div style={S.headerActions}>
          {dirtySaved && <button type="button" data-testid="qb-discard" style={{ ...btnS, color: C.danger }} disabled={locked} onClick={handleDiscard}>Discard edits</button>}
          <button type="button" data-testid="qb-new" style={btnS} disabled={locked} onClick={handleNew}>New</button>
          <button type="button" style={btnS} disabled={locked} onClick={onOpenSaved}>Saved Quotes</button>
        </div>
      </div>

      {message && <div style={S.notice}>{message}</div>}
      {dirtySaved && <div data-testid="qb-unsaved" style={S.unsavedNote}>Edits on this quote aren't saved yet. {existingRecord ? "Preview and Update in Quotes saves them." : "Save draft saves them."}</div>}

      <div style={{ ...S.main, ...(locked ? S.lockedForm : null) }} inert={locked ? "" : undefined}>
          {ENABLE_AI_INPUT && (
            <QuoteBoxPanel
              state={boxState}
              disabled={locked}
              onStart={startBox}
              onApply={() => boxRef.current?.apply()}
              onCancel={() => boxRef.current?.cancel()}
            />
          )}
          <section className="qb-panel" style={S.panel}>
            <PanelTitle title="Quote Details" />
            <div style={S.formGrid}>
              <Field label="Type">
                <select style={S.field} value={quote.docType || "quote"} onChange={e => set("docType", e.target.value)}>
                  {Object.entries(DOC_TYPES).map(([id, doc]) => <option key={id} value={id}>{doc.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select style={S.field} value={quote.jobStatus || "quoted"} onChange={e => set("jobStatus", e.target.value)}>
                  {Object.entries(QUOTE_STATUS_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </Field>
              <Field label="Quote Date">
                <input type="date" style={S.field} value={quote.quoteDate || ""} onChange={e => set("quoteDate", e.target.value)} />
              </Field>
              <Field label="Production (optional)">
                <input
                  style={S.field}
                  value={quote.productionName || ""}
                  onChange={e => set("productionName", e.target.value)}
                  placeholder="Show / project codename"
                />
              </Field>
              <PickerField label="Client">
                <ClientPicker
                  value={quote.clientName}
                  linked={linkedClient}
                  clients={clients}
                  jobStats={jobStatsByClient}
                  suggested={suggestedClient}
                  unlinkWarn={clientUnlinkWarn}
                  defaultDomain={(() => { const d = linkedContact ? emailDomain(primaryContactEmail(linkedContact)) : ""; return isOrgDomain(d) ? d : ""; })()}
                  defaultIndustry={quote.industry || "other"}
                  onPick={pickClient}
                  onType={typeClient}
                  onUnlink={unlinkClient}
                  onAddNew={addAndLinkClient}
                />
              </PickerField>
              <PickerField label="Contact">
                <ContactPicker
                  value={quote.contactName}
                  linked={linkedContact}
                  contacts={contacts}
                  onPick={pickContact}
                  onType={typeContact}
                  onUnlink={unlinkContact}
                  onAddNew={addAndLinkContact}
                />
                {!contactLinked && quote.contactName?.trim() && (
                  <div style={{ fontSize: 11, color: C.amber, marginTop: 4 }}>
                    Not linked yet. Pick them from the list, or add them as a new contact. The quote can't be ingested until the contact is linked.
                  </div>
                )}
              </PickerField>
              <label style={{ display: "block", minWidth: 0 }}>
                <span style={{ ...lbl, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                  <span>Capture Date</span>
                  <span style={{ display: "inline-flex", gap: 5 }}>
                    <MiniToggle on={!!quote.multiDay} color={C.blue} onClick={(e) => { e.preventDefault(); toggleMultiDay(); }}>Multi-day</MiniToggle>
                    <MiniToggle on={!!quote.scanDateTBD} color={C.amber} disabled={!!quote.multiDay} onClick={(e) => { e.preventDefault(); changeSchedule({ tbd: !quote.scanDateTBD }); }}>TBD</MiniToggle>
                  </span>
                </span>
                {quote.multiDay ? (
                  <input style={{ ...S.field, opacity: 0.7 }} value={sessionRange} readOnly disabled title="Set by the session dates below" />
                ) : (
                  <input type="date" style={{ ...S.field, opacity: quote.scanDateTBD ? 0.45 : 1 }} disabled={!!quote.scanDateTBD} value={quote.scanDate || ""} onChange={e => changeSchedule({ scanDate: e.target.value })} />
                )}
              </label>
              <FieldTBD label="Turnaround" tbd={!!quote.turnaroundTBD} onTBD={v => set("turnaroundTBD", v)}>
                <select style={{ ...S.field, opacity: quote.turnaroundTBD ? 0.45 : 1 }} disabled={!!quote.turnaroundTBD} value={quote.turnaround || "3-5"} onChange={e => setMany({ turnaround: e.target.value, turnaroundUnit: "businessDays" })}>
                  {/* Legacy quotes read in "weeks"; keep their (possibly off-preset) value selectable with the right unit. Changing it re-quotes in business days. */}
                  {(quote.turnaround && !TURNAROUND_OPTIONS.includes(quote.turnaround) ? [quote.turnaround, ...TURNAROUND_OPTIONS] : TURNAROUND_OPTIONS)
                    .map(o => <option key={o} value={o}>{o.replace("-", "–")} {quote.turnaroundUnit === "weeks" ? "weeks" : "business days"}</option>)}
                </select>
              </FieldTBD>
              <Field label="Follow-up">
                <input type="date" style={S.field} value={quote.followUpDate || ""} onChange={e => set("followUpDate", e.target.value)} />
              </Field>
              <Field label="Industry">
                <select style={S.field} value={quote.industry || "other"} onChange={e => set("industry", e.target.value)}>
                  {Object.entries(INDUSTRY_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </Field>
              <Field label="Lead Source">
                <select style={S.field} value={quote.leadSource || "referral"} onChange={e => set("leadSource", e.target.value)}>
                  {Object.entries(LEAD_SOURCES).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
                </select>
              </Field>
              {quote.leadSource === "referral" && (
                <Field label="Referred By">
                  <input style={S.field} value={quote.referredBy || ""} onChange={e => set("referredBy", e.target.value)} placeholder="Who referred them?" />
                </Field>
              )}
            </div>
            {/* Holds (2026-09-04, approved mockup holds-v1 Frame A): the live conflict
                lines under the capture date, the Equipment and Crew chips, and the
                "Hold these dates" switch. Internal fields: nothing here prints on the
                PDF, and nothing here blocks a save. */}
            <div className="quote-builder-hold" style={S.holdBlock}>
              {holdConflictLines.map((line) => (
                <div key={line} data-testid="qb-conflict-line" style={S.holdWarn}><span style={S.holdBang}>!</span><span>{line}</span></div>
              ))}
              {holdConflictLines.length === 0 && !quote.holdOff && holdDates.length > 0 && (
                <div data-testid="qb-conflict-ok" style={S.holdOk}><span style={{ ...S.holdBang, ...S.holdBangOk }}>✓</span><span>No conflict on {describeHoldDates(holdDates)}.</span></div>
              )}
              {unknownEquipmentIds.map((id) => (
                <div key={id} data-testid="qb-equipment-warning" style={S.holdWarn}><span style={S.holdBang}>!</span><span>Equipment not recognized for conflict checks: {equipmentLabel(id, equipmentMap)}</span></div>
              ))}
              <div>
                <span style={lbl}>Equipment</span>
                <div style={S.chipRow}>
                  {equipmentOptions.map((o) => {
                    const on = effectiveEquipmentIds.includes(o.id);
                    return (
                      <button key={o.id} type="button" data-testid={`qb-equipment-${o.id}`} aria-pressed={on} style={{ ...S.chip, ...(on ? S.chipOn : {}) }} onClick={() => toggleEquipment(o.id)}>{o.label}</button>
                    );
                  })}
                  {unknownEquipmentIds.map((id) => (
                    <button key={id} type="button" data-testid={`qb-equipment-${id}`} aria-pressed={true} style={{ ...S.chip, ...S.chipOn }} onClick={() => toggleEquipment(id)}>{equipmentLabel(id, equipmentMap)}</button>
                  ))}
                </div>
              </div>
              <div>
                <span style={lbl}>Crew (tentative)</span>
                <div style={S.chipRow}>
                  {crewOptions.map((m) => {
                    const on = crewIds.includes(m.id);
                    return (
                      <button key={m.id} type="button" data-testid={`qb-crew-${m.id}`} aria-pressed={on} style={{ ...S.chip, ...(on ? S.chipOn : {}) }} onClick={() => toggleCrew(m)}>{m.name}</button>
                    );
                  })}
                  {crewOptions.length === 0 && <span style={S.holdHint}>No crew on the Settings roster yet.</span>}
                </div>
              </div>
              <div style={S.switchRow}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!quote.holdOff}
                  aria-label="Hold these dates"
                  data-testid="qb-hold-switch"
                  style={{ ...S.switchTrack, ...(quote.holdOff ? {} : S.switchTrackOn) }}
                  onClick={() => set("holdOff", !quote.holdOff)}
                >
                  <span style={{ ...S.switchKnob, ...(quote.holdOff ? {} : S.switchKnobOn) }} />
                </button>
                <div>
                  <div style={S.switchTitle}>Hold these dates</div>
                  <div style={S.switchCap}>
                    {holdDates.length
                      ? `This quote holds ${describeHoldDates(holdDates)} on the calendar while it is open. Lost, archived, or trashed releases the hold. Book turns it into the job.`
                      : "This quote has no dated sessions to hold."}
                  </div>
                </div>
              </div>
            </div>
            {/* Single-day: job-level location. Multi-day: per-session locations rule, so this is replaced by the Session Dates card. */}
            {!quote.multiDay && (
              <div style={S.locationRow}>
                <label style={S.checkLabel}>
                  <input type="checkbox" checked={!!quote.isStudio} onChange={e => handleStudio(e.target.checked)} />
                  <span>Studio</span>
                </label>
                <input style={S.field} value={quote.jobLocation || ""} onChange={e => changeSchedule({ location: e.target.value })} placeholder="Location" />
              </div>
            )}
            {quote.multiDay && (
              <SessionDatesCard
                sessions={sortedSessions}
                summary={sessionSummary}
                error={sessionError}
                addDayOpen={addDayOpen}
                setAddDayOpen={setAddDayOpen}
                onUpdate={updateSession}
                onRemove={removeSession}
                onAdd={addSession}
              />
            )}
            <Field label="Summary">
              <textarea rows={3} style={S.textarea} value={quote.summary || ""} onChange={e => set("summary", e.target.value)} />
            </Field>
          </section>

          {contactMatches.length > 0 && (
            <section className="qb-panel" style={S.panel}>
              <PanelTitle title="Contacts" />
              <ContactMatchPanel
                matches={contactMatches}
                allConfirmed={matchesAllConfirmed}
                onConfirm={onConfirmMatch}
                onReject={onRejectMatch}
                onIgnore={onIgnoreMatch}
                onUndo={onUndoMatch}
                onConfirmAll={onConfirmAllMatches}
              />
            </section>
          )}

          <section className="qb-panel" style={S.panel}>
            <div style={S.panelHead}>
              <div style={S.panelLeft}>
                <PanelTitle title="Line Items" />
                {lineGroups.length > 0 && (
                  <span style={S.addingTo}>
                    Adding to:
                    <select aria-label="Adding to" data-testid="qb-adding-to" style={S.addingToSelect} value={activeBlock ? activeBlock.label : ""} onChange={(e) => setActiveGroup(e.target.value)}>
                      {lineGroups.map((g) => <option key={g.label} value={g.label}>{g.label}</option>)}
                      <option value="">No block</option>
                    </select>
                  </span>
                )}
              </div>
              <div style={S.actions}>
                <button type="button" data-testid="qb-new-block" style={{ ...btnS, ...(showBlockCreator ? S.btnOpen : {}) }} onClick={() => setShowBlockCreator((v) => !v)}>New block</button>
                <button type="button" style={btnS} onClick={() => setShowCatalog(v => !v)}>{showCatalog ? "Close Catalog" : "Catalog"}</button>
                <button type="button" style={btnS} onClick={() => (showLidar ? closeLidar() : openLidar())}>{showLidar ? "Close LiDAR" : "LiDAR"}</button>
                <button type="button" style={btnS} onClick={() => setShowDrone(v => !v)}>{showDrone ? "Close Drone" : "Drone"}</button>
                <button type="button" style={btnS} onClick={addCustom}>Custom</button>
              </div>
            </div>
            {/* LiDAR handoff: the AI flagged LiDAR scope but is not allowed to price it —
                route the operator to the estimator. With blocks from the brief it shows
                progress and opens the next pending block; clears once every block is in. */}
            {quote.lidarDetected && (() => {
              const blocks = normalizeLidarBlocks(quote.lidarPrefill) || [];
              const pending = lidarPending;
              if (blocks.length ? pending.length === 0 : hasLidarLineItem(lineItems)) return null;
              const nextBlock = pending[0];
              return (
                <div style={S.lidarBanner} data-testid="lidar-banner">
                  <span>
                    {blocks.length > 1 ? (
                      <><strong style={{ fontWeight: 500 }}>{blocks.length} LiDAR blocks in the brief.</strong> {blocks.length - pending.length} of {blocks.length} inserted
                        <span style={S.lidarProg} aria-hidden="true">{blocks.map((_, i) => <i key={i} style={{ ...S.lidarDot, ...(pending.some((p) => p.i === i) ? {} : S.lidarDotOn) }} />)}</span></>
                    ) : blocks.length === 1 ? (
                      <><strong style={{ fontWeight: 500 }}>LiDAR scope detected.</strong> The estimator is pre-filled from the brief; check it and insert the lines.</>
                    ) : (
                      <><strong style={{ fontWeight: 500 }}>LiDAR scope detected.</strong> LiDAR is priced with the estimator, not by the AI — this quote has no LiDAR line yet.</>
                    )}
                  </span>
                  <button type="button" style={{ ...btnP, whiteSpace: "nowrap" }} onClick={openLidar}>
                    {blocks.length > 1 && nextBlock ? `Continue with block ${nextBlock.i + 1}: ${lidarBlockName(nextBlock.block, nextBlock.i)}` : "Open LiDAR Estimator"}
                  </button>
                </div>
              );
            })()}
            {showCatalog && <CatalogBrowser onPick={addFromCatalog} />}
            {showLidar && (
              // Keyed on the quote and the blocks' FACTS: a new extraction remounts the widget with
              // its new blocks, while a label written back onto a block by Insert does not.
              // Opened for a target block (no brief block pending): no prefill, the target seeds
              // the Block fields and follows "Adding to" while open. Pending brief blocks always
              // win, so the walk runs unchanged.
              <LidarEstimator key={`${q?.id || "new"}:${JSON.stringify((normalizeLidarBlocks(quote.lidarPrefill) || []).map(({ label: _l, note: _n, ...facts }) => facts))}`}
                initial={lidarTarget ? null : (quote.lidarPrefill || null)} target={lidarTarget} industry={quote.industry || "other"}
                lineItems={lineItems} onInsert={addLidarLineItems} onClose={closeLidar} />
            )}
            {showDrone && (
              // The "Adding to" block seeds the Block fields and follows the pill while open;
              // no brief walk exists for drone, so `initial` stays null and the target rules.
              <DroneEstimator key={q?.id || "new"} initial={null} target={droneTarget} industry={quote.industry || "other"}
                lineItems={lineItems} onInsert={addDroneLineItems} onClose={() => setShowDrone(false)} />
            )}
            <LineItemsTable
              items={lineItems}
              discounts={quoteDiscounts}
              lineGroups={lineGroups}
              onGroupNoteUpdate={setGroupNote}
              onGroupPrintLabelClear={clearGroupPrintLabel}
              onRemoveGroup={removeGroup}
              onUpdate={updateItem}
              onRemove={removeItem}
              onToggleDiscount={toggleLineDiscount}
              onDiscountUpdate={updateLineDiscount}
              onAddQuoteDiscount={addQuoteDiscount}
              onQuoteDiscountUpdate={updateQuoteDiscount}
              onQuoteDiscountRemove={removeQuoteDiscount}
            />
            {showBlockCreator && (
              <BlockCreator existing={lineGroups.map((g) => g.label)} brief={briefSuggestions} schedule={scheduleSuggestions}
                onCreate={createGroup} onCancel={() => setShowBlockCreator(false)} />
            )}
            <TotalsDock totals={totals} proBono={isProBono(quote)} onToggleProBono={toggleProBono} proBonoMismatch={proBonoMismatch} />
          </section>

          <section className="qb-panel" style={S.panel}>
            <PanelTitle title="Deliverables" />
            <div style={S.formatGrid}>
              {DELIVERABLE_FORMATS.map(format => (
                <label key={format.id} style={S.formatChip}>
                  <input
                    type="checkbox"
                    checked={(quote.deliverableFormats || []).includes(format.id)}
                    onChange={() => toggleFormat(format.id)}
                  />
                  <span>{format.label}</span>
                </label>
              ))}
            </div>
            <Field label="Deliverable Text">
              <textarea rows={3} style={S.textarea} value={quote.deliverableText || ""} onChange={e => set("deliverableText", e.target.value)} />
            </Field>
            <Field label="Internal Notes">
              <textarea rows={3} style={S.textarea} value={quote.internalNotes || ""} onChange={e => set("internalNotes", e.target.value)} />
            </Field>
            <div style={S.pricingToggle}>
              <input type="checkbox" checked={pricing.checked} onChange={e => set("includePricingSheet", e.target.checked)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 12, color: C.text }}>Include volume pricing sheet</div>
                <div style={S.pricingHint}>
                  {pricing.isAuto && pricing.qualifies
                    ? (<><span style={S.autoPill}>Auto</span> Multi-quantity scans on this quote — the tier-rate page is added automatically. Uncheck to omit.</>)
                    : "Adds a tier-rate page to the PDF."}
                </div>
              </div>
            </div>
          </section>

          <section className="qb-panel" style={S.panel}>
            <PanelTitle title="Attachments" />
            <AttachmentsPanel
              attachments={attachments}
              uploadStates={uploadStates}
              rejectedFiles={rejectedFiles}
              fileInputRef={fileInputRef}
              onAdd={addAttachments}
              onRemove={removeAttachment}
              onRetry={retryAttachment}
              onToggleAddendum={toggleAttachmentAddendum}
              onCaption={setAttachmentCaption}
              onDismissRejected={dismissRejected}
            />
          </section>

          <section className="qb-panel" style={S.panel}>
            <PanelTitle title="Document Sections" />
            <div style={S.sectionsHelp}>The clauses that print on the client PDF. Toggle what appears; edit any text to tailor this quote. Defaults restore on a new quote.</div>
            {SECTION_ORDER.map((key, idx) => {
              const def = SECTION_DEFS[key];
              if (!def) return null;
              const locked = isSectionLocked(quote, key);
              const isOn = isSectionOn(quote, key);
              const studioOff = key === "travel" && !!quote.isStudio && !isOn;
              return (
                <div key={key} style={{ ...S.sectionRow, ...(idx === 0 ? S.sectionRowFirst : null) }}>
                  <label className="quote-builder-section-head" style={S.sectionHeadRow}>
                    <input type="checkbox" checked={isOn} disabled={locked} onChange={() => toggleSection(key)} style={{ margin: 0 }} />
                    <span style={isOn ? S.sectionName : S.sectionNameOff}>{def.label}</span>
                    {def.alwaysOn && <span style={S.badgeOn}>always on</span>}
                    {key === "cancellation" && getDeposit(quote).type !== "none" && <span style={S.badgeDep}>linked to deposit</span>}
                    {studioOff && <span style={S.badgeStudio}>off for studio</span>}
                  </label>
                  {isOn && def.generated && (
                    // Generated sections have no editable body — the sentences are built
                    // from quote fields + line items at print time (turnaroundLines), so
                    // the editor shows exactly what the PDF prints.
                    <div style={{ ...S.sectionText, opacity: 0.7 }}>
                      {(() => {
                        const lines = turnaroundLines(quote);
                        return lines.length
                          ? lines.map((t, i) => <div key={i}>{t}</div>)
                          : <div>No turnaround line prints: capture only, no processing line.</div>;
                      })()}
                      {hasEstimatorLineItem(quote.lineItems) && !showsArrayTurnaround(quote.lineItems) && (
                        <div style={{ fontSize: 11, marginTop: 4 }}>The raw-models sentence is hidden because this quote carries only LiDAR or drone work.</div>
                      )}
                    </div>
                  )}
                  {isOn && !def.generated && (
                    <textarea
                      rows={2}
                      style={S.sectionText}
                      // Travel shows the sentence that will print (it follows the travel
                      // line items unless the operator typed their own), so the editor and
                      // the PDF cannot disagree.
                      value={key === "travel" ? travelClauseText(quote) : (sections[key]?.text ?? def.defaultText)}
                      onChange={e => updateSectionText(key, e.target.value)}
                    />
                  )}
                </div>
              );
            })}
            <CustomWordingList quote={quote} onReset={(key) => setMany(resetWordingPatch(quote, key))} />
          </section>

          <div className="quote-builder-footer" style={S.footer}>
            <span style={S.footerHint}>{uploadsPending ? "Uploading images… ingest waits until they finish." : "Done? Preview to review, then ingest."}</span>
            <button
              type="button"
              style={{ ...btnS, color: C.amber, borderColor: `${C.amber}45`, opacity: saving || locked || uploadsPending ? 0.45 : 1 }}
              disabled={saving || locked || uploadsPending}
              title={uploadsPending ? "Images are still uploading" : undefined}
              onClick={handleSaveDraft}
            >
              {saving || locked ? "Saving…" : "Save draft"}
            </button>
            <button type="button" style={btnP} disabled={locked} onClick={() => setShowPdf(true)}>Preview →</button>
          </div>
        </div>

      {showPdf && (
        <QuotePdfPreview
          quote={quote}
          onClose={() => setShowPdf(false)}
          onDone={() => { setShowPdf(false); onOpenSaved?.(); }}
          onIngest={handleIngestFromPreview}
          canIngest={canSave && !uploadsPending && !uploadsFailed}
          ingestLabel={existingRecord ? "Update in Quotes" : "Ingest into Quotes →"}
          lock={quoteBusy}
          ingestDisabledReason={uploadsPending ? "Images are still uploading" : uploadsFailed ? "An attachment failed to upload — retry or remove it" : !contactLinked ? "Link the contact first: pick them from your contacts or add them as new" : !canSave ? "Add a client, contact, and a line item first" : ""}
        />
      )}
    </div>
  );
}

function PanelTitle({ title }) {
  return (
    <div style={S.panelTitleRow}>
      <span style={S.panelAccent} />
      <span style={S.panelTitle}>{title}</span>
    </div>
  );
}

// CRM contact-match review (Step 4 Slice 2). People/companies the AI pulled from
// the email, checked against the CRM. Per row: Link an existing record, mark New
// (create on book), or No Link (skip). The amber strip is a soft nudge only.
function ContactMatchPanel({ matches, allConfirmed, onConfirm, onReject, onIgnore, onUndo, onConfirmAll }) {
  return (
    <div>
      <div style={S.cmHelp}>
        People &amp; companies the AI found, checked against your CRM. <strong style={{ fontWeight: 500, color: C.text }}>Link</strong> someone you already have, mark <strong style={{ fontWeight: 500, color: C.text }}>New</strong> to create them, or <strong style={{ fontWeight: 500, color: C.text }}>No Link</strong> to skip. The quote's contact must be linked, here or in the Contact field; the others are optional.
      </div>
      <div style={{ ...S.cmCard, ...(allConfirmed ? null : S.cmCardPending) }}>
        <div className="quote-builder-contact-head" style={{ ...S.cmHead, ...(allConfirmed ? null : S.cmHeadPending) }}>
          <div style={S.cmHeadL}>
            <span style={S.cmHeadLab}>Contacts Found</span>
            {!allConfirmed && <span style={S.cmHeadSub}>· review to confirm</span>}
          </div>
          {!allConfirmed && (
            <button type="button" style={S.cmLinkAll} onClick={onConfirmAll}>Link All Matches</button>
          )}
        </div>
        {matches.map((m, i) => {
          // Defensive: a malformed/legacy-flat row may lack `.extracted`.
          const ex = m.extracted || {};
          const detail = ex.email || ex.url ||
            (ex.role === "client" ? "company" : ex.role) || "";
          return (
            <div key={i} className="quote-builder-contact-row" style={S.cmRow}>
              <div style={S.cmWho}>
                <span style={{ ...S.cmDot, background: confColor(m.confidence) }} />
                <div style={{ minWidth: 0 }}>
                  <div style={S.cmName}>{ex.name || "(unnamed)"}</div>
                  {detail && <div style={S.cmDetail}>{detail}</div>}
                </div>
              </div>
              <div className="quote-builder-contact-verdict" style={S.cmVerdict}>
                {m.matchName
                  ? (<><span style={{ color: confColor(m.confidence) }}>{m.confidence}</span> → {m.matchName}</>)
                  : <span>No match</span>}
              </div>
              <div className="quote-builder-contact-actions" style={S.cmActs}>
                {!m.confirmed ? (
                  <>
                    {m.matchId && (
                      <button type="button" style={{ ...S.cmBtn, ...S.cmBtnLink }} onClick={() => onConfirm(i)}>Link</button>
                    )}
                    <button type="button" style={S.cmBtn} onClick={() => onReject(i)}>New</button>
                    <button type="button" style={S.cmBtn} onClick={() => onIgnore(i)}>No Link</button>
                  </>
                ) : (
                  <button type="button" style={S.cmUndo} onClick={() => onUndo(i)}>
                    <span style={{ color: m.ignored ? C.muted : C.green }}>
                      {m.ignored ? "No Link" : (m.matchId ? "Linked" : "Creating")}
                    </span>
                    <span style={S.cmUndoHint}>← undo</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", minWidth: 0 }}>
      <span style={lbl}>{label}</span>
      {children}
    </label>
  );
}

// Field for the Client and Contact pickers: a div, not a label. Inside a label,
// Safari passes a button click on to the first button the label then holds, so
// "Add & link" re-rendered into the linked chip and Safari clicked its
// "Unlink" at once (the new contact never stayed linked). The pickers name
// their own search input instead (aria-label).
function PickerField({ label, children }) {
  return (
    <div role="group" aria-label={label} style={{ display: "block", minWidth: 0 }}>
      <span style={lbl}>{label}</span>
      {children}
    </div>
  );
}

// F8 — searchable Contact picker. Type → live CRM matches (name/email/company) → pick
// to LINK, or "+ Add new contact" (email optional). Falls back to plain free text if the
// operator just types and never picks (back-compat with the old text field).

/* Client picker — the account-side twin of ContactPicker below. Same three
   states (linked chip / add-new form / type-to-search), same CP styles, plus:
   job-history line per row (the repeat-client signal), a suggest-don't-fill
   chip fed by the linked contact's org domain, and the CD7 unlink warning. */
function ClientPicker({ value, linked, clients, jobStats, suggested, unlinkWarn, defaultDomain, defaultIndustry, onPick, onType, onUnlink, onAddNew }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [studioQuery, setStudioQuery] = useState("");
  const [studioPick, setStudioPick] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Two separate counts (Codex design review, 2026-08-26): the payer's own jobs,
  // then the associated-studio family — recognition aid only, never selection.
  const histLine = (c) => {
    const st = jobStats?.get?.(c.id);
    const fam = familyJobStats(c, clients, jobStats);
    const parts = [];
    if (st) parts.push(`${st.count} job${st.count === 1 ? "" : "s"}`);
    if (fam.family > fam.own) {
      const studio = (clients || []).find(x => x.id === c.parentClientId);
      parts.push(`${fam.family} across ${studio ? studio.name : "family"}${studio ? " family" : ""}`);
    }
    if (st?.last) parts.push(`last ${fmtDate(st.last)}`);
    return parts.length ? parts.join(" · ") : null;
  };
  const studioOf = (c) => (clients || []).find(x => x.id === c?.parentClientId) || null;

  const q = (value || "").trim().toLowerCase();
  // Same per-keystroke budget as the contact picker: scan only while open with a
  // query, stop at 6. Searches name, domain, and client code.
  const matches = useMemo(() => {
    if (!open || !q) return [];
    const out = [];
    for (const c of clients || []) {
      const name = (c.name || "").toLowerCase();
      const dom = (c.orgDomain || "").toLowerCase();
      const code = (c.clientCode || "").toLowerCase();
      if (name.includes(q) || dom.includes(q) || code.includes(q)) {
        out.push(c);
        if (out.length >= 6) break;
      }
    }
    return out;
  }, [clients, q, open]);

  if (linked) {
    const hist = histLine(linked);
    const studio = studioOf(linked);
    return (
      <div>
        <div style={CP.linked}>
          <span style={CP.avatar}>{initials(linked.name)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={CP.linkedName}>{linked.name || "(unnamed)"}</div>
            <div style={CP.sub}>{linked.orgDomain || "no website"}{hist ? ` · ${hist}` : ""}</div>
          </div>
          <button type="button" style={CP.unlink} onClick={onUnlink} title="Unlink client">Unlink ✕</button>
        </div>
        {studio && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, padding: "2px 9px", borderRadius: 99, fontSize: 10.5, color: C.purple, background: `${C.purple}14`, border: `1px solid ${C.purple}4D` }}>
            Associated studio: <b>{studio.name}</b>
          </span>
        )}
      </div>
    );
  }

  if (adding) {
    const canAdd = (value || "").trim().length > 0;
    // "Associated studio" type-ahead — one level only, so a client that already
    // has a studio of its own is not offered as a studio.
    const sq = studioQuery.trim().toLowerCase();
    const studioMatches = sq && !studioPick
      ? (clients || []).filter(c => !c.parentClientId && (c.name || "").toLowerCase().includes(sq)).slice(0, 5)
      : [];
    const resetAdd = () => { setAdding(false); setNewDomain(""); setStudioQuery(""); setStudioPick(null); };
    return (
      <div style={CP.addForm} onMouseDown={e => e.stopPropagation()}>
        <input style={S.field} autoFocus value={value || ""} onChange={e => onType(e.target.value)} placeholder="Client name *" aria-label="New client name" />
        {/* Stacked, not side-by-side: this column is narrow and clipped
            placeholders made the studio box unreadable (verified live). */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 6, marginTop: 6 }}>
          <input style={S.field} value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="Website / domain (optional)" aria-label="New client website" />
          <div style={{ position: "relative" }}>
            {studioPick ? (
              <div style={{ ...S.field, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{studioPick.name}</span>
                <button type="button" style={{ border: "none", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 12 }} onClick={() => { setStudioPick(null); setStudioQuery(""); }} title="Clear studio">✕</button>
              </div>
            ) : (
              <input style={S.field} value={studioQuery} onChange={e => setStudioQuery(e.target.value)} placeholder="Associated studio (optional)" aria-label="Associated studio" />
            )}
            {studioMatches.length > 0 && (
              <div style={CP.drop}>
                {studioMatches.map(c => (
                  <div key={c.id} style={CP.opt} onMouseDown={e => { e.preventDefault(); setStudioPick(c); setStudioQuery(""); }}>
                    <span style={CP.avatar}>{initials(c.name)}</span>
                    <div style={CP.optName}>{c.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" style={btnS} onClick={resetAdd}>Cancel</button>
          <button
            type="button"
            style={{ ...btnP, opacity: canAdd ? 1 : 0.5 }}
            disabled={!canAdd}
            onClick={async () => { await onAddNew({ name: value, domain: newDomain, industry: defaultIndustry, parentClientId: studioPick?.id || "" }); resetAdd(); }}
          >Add &amp; link</button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        aria-label="Client"
        style={S.field}
        value={value || ""}
        onChange={e => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search clients…"
        autoComplete="off"
      />
      {open && q.length > 0 && (
        <div style={CP.drop}>
          {matches.length > 0 && <div style={CP.dropSec}>Matches in your CRM</div>}
          {matches.map(c => {
            const hist = histLine(c);
            const studio = studioOf(c);
            return (
              <div key={c.id} style={CP.opt} onMouseDown={e => { e.preventDefault(); onPick(c); setOpen(false); }}>
                <span style={CP.avatar}>{initials(c.name)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={CP.optName}>{c.name || "(unnamed)"}</div>
                  <div style={CP.sub}>{studio ? `studio: ${studio.name} · ` : ""}{c.orgDomain || "no website"}{hist ? ` · ${hist}` : ""}</div>
                </div>
              </div>
            );
          })}
          <div style={{ ...CP.opt, ...CP.add }} onMouseDown={e => { e.preventDefault(); setOpen(false); setNewDomain(defaultDomain || ""); setAdding(true); }}>
            <span style={{ ...CP.avatar, ...CP.addAvatar }}>+</span>
            <div style={{ minWidth: 0 }}>
              <div style={CP.addName}>{`Add \u201C${(value || "").trim()}\u201D as a new client`}</div>
              <div style={CP.sub}>create a CRM account — website optional</div>
            </div>
          </div>
        </div>
      )}
      {suggested && !open && (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, padding: "6px 9px", borderRadius: 6, background: `${C.blue}0D`, border: `1px solid ${C.blue}35`, cursor: "pointer" }}
          onMouseDown={e => { e.preventDefault(); onPick(suggested); }}
          title="Link this client"
        >
          <span style={{ fontSize: 11, color: C.blue, fontWeight: 500 }}>Looks like {suggested.name}</span>
          <span style={{ fontSize: 11, color: C.muted }}>{suggested.orgDomain || ""} — click to link</span>
        </div>
      )}
      {unlinkWarn && !open && (
        <div style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 6, padding: "6px 9px", borderRadius: 6, background: `${C.amber}12`, border: `1px solid ${C.amber}40`, fontSize: 11, color: C.text, lineHeight: 1.45 }}>
          <span style={{ color: C.amber }}>⚠</span>
          <span>Unlinked from <b>{unlinkWarn}</b> — this quote won’t attach to that account unless you pick it again.</span>
        </div>
      )}
    </div>
  );
}

function ContactPicker({ value, linked, contacts, onPick, onType, onUnlink, onAddNew }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = (value || "").trim().toLowerCase();
  // Scan the CRM only while the dropdown is open with a query — this sits in the
  // builder's per-keystroke render path and contacts is thousands of rows, so stop
  // at 6 matches instead of filtering the whole array on every render.
  const matches = useMemo(() => {
    if (!open || !q) return [];
    const out = [];
    for (const c of contacts || []) {
      const name = (c.name || "").toLowerCase();
      const emails = (Array.isArray(c.emails) ? c.emails.join(" ") : (c.email || "")).toLowerCase();
      const company = (c.company || "").toLowerCase();
      if (name.includes(q) || emails.includes(q) || company.includes(q)) {
        out.push(c);
        if (out.length >= 6) break;
      }
    }
    return out;
  }, [contacts, q, open]);

  // LINKED — show the resolved CRM record with an unlink affordance.
  if (linked) {
    const email = primaryContactEmail(linked);
    return (
      <div style={CP.linked}>
        <span style={CP.avatar}>{initials(linked.name)}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={CP.linkedName}>{linked.name || "(unnamed)"}</div>
          <div style={CP.sub}>{email || "no email"}{linked.company ? ` · ${linked.company}` : ""}</div>
        </div>
        <button type="button" style={CP.unlink} onClick={onUnlink} title="Unlink contact">Unlink ✕</button>
      </div>
    );
  }

  // ADD-NEW — name (prefilled from the typed text) required, email + company optional.
  if (adding) {
    const canAdd = (value || "").trim().length > 0;
    return (
      <div style={CP.addForm} onMouseDown={e => e.stopPropagation()}>
        <input style={S.field} autoFocus value={value || ""} onChange={e => onType(e.target.value)} placeholder="Full name *" aria-label="New contact name" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
          <input style={S.field} value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email (optional)" aria-label="New contact email" />
          <input style={S.field} value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="Company (optional)" aria-label="New contact company" />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button type="button" style={btnS} onClick={() => { setAdding(false); setNewEmail(""); setNewCompany(""); }}>Cancel</button>
          <button
            type="button"
            style={{ ...btnP, opacity: canAdd ? 1 : 0.5 }}
            disabled={!canAdd}
            onClick={async () => { await onAddNew({ name: value, email: newEmail, company: newCompany }); setAdding(false); setNewEmail(""); setNewCompany(""); }}
          >Add &amp; link</button>
        </div>
      </div>
    );
  }

  // SEARCH — type-to-filter; pick a match or open the add-new form.
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <input
        aria-label="Contact"
        style={S.field}
        value={value || ""}
        onChange={e => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search name or email…"
        autoComplete="off"
      />
      {open && q.length > 0 && (
        <div style={CP.drop}>
          {matches.length > 0 && <div style={CP.dropSec}>Matches in your CRM</div>}
          {matches.map(c => {
            const email = primaryContactEmail(c);
            return (
              <button type="button" key={c.id} style={CP.opt} onMouseDown={e => e.preventDefault()} onClick={() => { onPick(c); setOpen(false); }}>
                <span style={CP.avatar}>{initials(c.name)}</span>
                <div style={{ minWidth: 0 }}>
                  <div style={CP.optName}>{c.name || "(unnamed)"}</div>
                  <div style={CP.sub}>{email || "no email"}{c.company ? ` · ${c.company}` : ""}</div>
                </div>
              </button>
            );
          })}
          <button type="button" style={{ ...CP.opt, ...CP.add }} onMouseDown={e => e.preventDefault()} onClick={() => { setOpen(false); setAdding(true); }}>
            <span style={{ ...CP.avatar, ...CP.addAvatar }}>+</span>
            <div style={{ minWidth: 0 }}>
              <div style={CP.addName}>{`Add “${(value || "").trim()}” as a new contact`}</div>
              <div style={CP.sub}>create a CRM contact — email optional</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

const CP = {
  linked: { display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", border: `1px solid ${C.green}55`, background: `${C.green}10`, borderRadius: 6 },
  linkedName: { fontSize: 13, fontWeight: 500, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  unlink: { fontSize: 11, color: C.danger, background: "transparent", border: `1px solid ${C.danger}40`, borderRadius: 5, padding: "4px 8px", cursor: "pointer", fontFamily: font, flexShrink: 0 },
  addForm: { border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, background: C.surface },
  drop: { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", boxShadow: "0 8px 24px rgba(26,26,26,0.10)" },
  dropSec: { fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, padding: "8px 11px 3px" },
  // Options are buttons (keyboard: Tab to one, Enter picks it), reset to look like rows.
  opt: { display: "flex", alignItems: "center", gap: 10, padding: "8px 11px", cursor: "pointer", width: "100%", textAlign: "left", background: "transparent", border: "none", borderRadius: 0, font: "inherit", color: "inherit" },
  optName: { fontSize: 12.5, fontWeight: 500, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sub: { fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  avatar: { width: 28, height: 28, borderRadius: "50%", background: C.surfaceAlt, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 500, flexShrink: 0 },
  add: { borderTop: `1px solid ${C.border}` },
  addAvatar: { background: `${C.blue}1a`, color: C.blue },
  addName: { fontSize: 12.5, fontWeight: 500, color: C.blue },
};

// Field with an inline "TBD" toggle in the label row (capture date, turnaround).
function FieldTBD({ label, tbd, onTBD, children }) {
  return (
    <label style={{ display: "block", minWidth: 0 }}>
      <span style={{ ...lbl, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{label}</span>
        <span
          onClick={(e) => { e.preventDefault(); onTBD(!tbd); }}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, textTransform: "none", letterSpacing: 0, fontSize: 10, color: tbd ? C.text : C.muted, cursor: "pointer" }}
        >
          <input type="checkbox" checked={tbd} readOnly style={{ margin: 0, pointerEvents: "none" }} /> TBD
        </span>
      </span>
      {children}
    </label>
  );
}

// Small pill toggle used in field label rows (Multi-day, TBD).
function MiniToggle({ on, color, disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: "2px 9px", fontSize: 9, fontWeight: 500, borderRadius: 4,
        cursor: disabled ? "not-allowed" : "pointer", letterSpacing: "0.06em", textTransform: "uppercase",
        border: `1px solid ${on ? color + "55" : C.border}`,
        background: on ? color + "12" : "transparent",
        color: on ? color : C.muted, opacity: disabled ? 0.5 : 1, fontFamily: font,
      }}
    >{children}</button>
  );
}

// Multi-day schedule grid: one row per session (date / type / location / in-studio),
// add by type, remove, with duplicate-day validation. Full parity with the legacy quote.
function SessionDatesCard({ sessions, summary, error, addDayOpen, setAddDayOpen, onUpdate, onRemove, onAdd }) {
  // Same outside-click close as the job card's AddDayMenu — this copy lacked it.
  const addDayRef = useRef(null);
  useEffect(() => {
    if (!addDayOpen) return undefined;
    const close = (e) => { if (addDayRef.current && !addDayRef.current.contains(e.target)) setAddDayOpen(false); };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [addDayOpen, setAddDayOpen]);
  const typeColors = SESSION_TYPE_COLORS(C);
  return (
    <div className="quote-builder-session-card" style={S.sessCard}>
      <div className="quote-builder-session-headline" style={S.sessHead}>
        <span style={S.sessTitle}>Session Dates</span>
        {summary && <span style={S.sessSummary}>{summary}</span>}
      </div>
      {sessions.length > 0 && (
        <div className="quote-builder-session-head" style={{ ...S.sessGrid, ...S.sessGridHead }}>
          <span />
          <span>Date</span>
          <span>Type</span>
          <span>Location</span>
          <span style={{ textAlign: "center" }}>In-Studio</span>
          <span />
        </div>
      )}
      {sessions.map((s, i) => {
        const color = typeColors[s.sessionType] || C.blue;
        return (
          <div key={s.id} className="quote-builder-session-row" style={{ ...S.sessGrid, ...S.sessRow }}>
            <span style={S.sessIdx}>{i + 1}.</span>
            <input type="date" style={S.sessInput} value={s.date || ""} onChange={e => onUpdate(s.id, { date: e.target.value })} />
            <select style={{ ...S.sessInput, color }} value={s.sessionType} onChange={e => onUpdate(s.id, { sessionType: e.target.value })}>
              {SESSION_TYPES.map(t => <option key={t} value={t} style={{ color: C.text }}>{SESSION_TYPE_LABELS[t]}</option>)}
            </select>
            <input
              style={{ ...S.sessInput, opacity: s.isStudio ? 0.6 : 1 }}
              value={s.isStudio ? STUDIO_ADDRESS : (s.location || "")}
              disabled={s.isStudio}
              placeholder="Venue / address"
              onChange={e => onUpdate(s.id, { location: e.target.value })}
            />
            <button
              type="button"
              onClick={() => onUpdate(s.id, s.isStudio ? clearLocationPatch() : studioPatch())}
              style={S.studioBtn(s.isStudio)}
            >{s.isStudio ? "On" : "Off"}</button>
            <button type="button" onClick={() => onRemove(s.id)} title="Remove session" style={S.sessRemove}>×</button>
          </div>
        );
      })}
      {error && <div style={S.sessError}>{error}</div>}
      <div ref={addDayRef} style={{ position: "relative", display: "inline-block", marginTop: 10 }}>
        <button type="button" style={{ ...btnS, padding: "6px 12px", fontSize: 11, color: C.blue, borderColor: `${C.blue}40` }} onClick={() => setAddDayOpen(v => !v)}>+ Add Day ▾</button>
        {addDayOpen && (
          <div style={S.addMenu}>
            {SESSION_TYPES.map(t => (
              <div
                key={t}
                style={S.addItem}
                onClick={() => onAdd(t)}
                onMouseEnter={e => (e.currentTarget.style.background = C.surfaceAlt)}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: typeColors[t], display: "inline-block", flexShrink: 0 }} />
                {SESSION_TYPE_LABELS[t]}
              </div>
            ))}
          </div>
        )}
      </div>
      {sessions.some(s => !s.isStudio) && (
        <div style={S.sessTravelNote}><span style={S.autoPill}>Auto</span> A session is on-location — the Travel clause is enabled in Document Sections.</div>
      )}
    </div>
  );
}

// Decode a (compressed) image dataUrl back into a named File for upload, so the
// durable Storage copy is the lightweight version we already display.
function dataUrlToFile(dataUrl, name) {
  const [head, b64] = dataUrl.split(",");
  const mime = (head.match(/data:(.*?);/) || [])[1] || "image/jpeg";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], name || "image", { type: mime });
}

// Human-readable file size.
function fmtBytes(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(b < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}
const fileExtLabel = (name) => ((name || "").split(".").pop() || "FILE").toUpperCase().slice(0, 4);
const FILE_ICONS = { pdf: "📄", doc: "📝" };

// Per-attachment display state: uploading / error / stored (durable url) /
// local (compressed in-session, not yet/again uploaded).
function attachmentState(att, uploadStates) {
  const live = uploadStates[att.id];
  if (live?.state === "uploading") return { kind: "uploading" };
  if (live?.state === "error") return { kind: "error", error: live.error };
  if (att.url) return { kind: "stored" };
  if (att.dataUrl) return { kind: "local" };
  return { kind: "none" };
}

// Attachments panel: reference images (caption + include-on-quote toggle, print as
// the PDF addendum) and files (PDF/DOC, ride along for reference + Step 4 AI).
// Durable upload to Firebase Storage; oversized files are rejected with a warning.
function AttachmentsPanel({ attachments, uploadStates, rejectedFiles, fileInputRef, onAdd, onRemove, onRetry, onToggleAddendum, onCaption, onDismissRejected }) {
  const [dragOver, setDragOver] = useState(false);
  const images = attachments.filter(a => isImageAttachment(a.type));
  const files = attachments.filter(a => !isImageAttachment(a.type));

  const pick = () => fileInputRef.current?.click();
  const onInput = (e) => { onAdd(e.target.files); e.target.value = ""; };
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) onAdd(e.dataTransfer.files);
  };

  return (
    <div>
      <div style={S.attHelp}>
        Reference images and files for this quote. Images marked <strong style={{ fontWeight: 500, color: C.text }}>Include on quote</strong> print as a “Reference Images” addendum on the client PDF. Files up to {Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024))}&nbsp;MB are stored with the quote and open from any device; larger files can’t be saved.
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={pick}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className="quote-builder-dropzone"
        style={{ ...S.dropzone, ...(dragOver ? S.dropzoneOver : null) }}
      >
        <span style={{ fontSize: 16, color: C.muted }}>＋</span>
        <span><strong style={{ fontWeight: 500, color: C.text }}>Add images or files</strong> · drag &amp; drop or click</span>
        <span style={S.dropAcc}>JPG · PNG · PDF · DOC</span>
      </div>
      <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={onInput} style={{ display: "none" }} />

      {images.length > 0 && (
        <>
          <div style={S.attSubhead}>Reference images · {images.length}</div>
          <div className="quote-builder-img-grid" style={S.imgGrid}>
            {images.map((att, i) => {
              const st = attachmentState(att, uploadStates);
              const src = att.dataUrl || att.url || null;
              return (
                <div key={att.id} style={S.imgCard}>
                  <div style={{ ...S.thumb, ...(src ? { backgroundImage: `url(${src})`, backgroundSize: "cover", backgroundPosition: "center" } : null) }}>
                    {!src && <span>No preview</span>}
                    <span style={S.badge}>{i + 1}</span>
                    <button type="button" title="Remove" onClick={() => onRemove(att.id)} style={S.thumbDel}>×</button>
                  </div>
                  <div style={S.cardBody}>
                    <input
                      style={S.capInput}
                      placeholder="Add caption…"
                      value={att.caption || ""}
                      onChange={e => onCaption(att.id, e.target.value)}
                      aria-label={`Caption for image ${i + 1}`}
                    />
                    <div style={S.cardFoot}>
                      <label style={S.inclLabel}>
                        <input type="checkbox" checked={!!att.includeInAddendum} onChange={() => onToggleAddendum(att.id)} style={{ margin: 0, accentColor: C.blue }} />
                        Include on quote
                      </label>
                      {att.aiRead ? <span style={S.aiReadPill}>✓ AI read</span> : <StateChip st={st} onRetry={() => onRetry(att.id)} />}
                    </div>
                    {st.kind === "uploading" && <div style={S.prog}><span style={S.progBar} /></div>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {files.length > 0 && (
        <>
          <div style={S.attSubhead}>Files · {files.length}</div>
          {files.map(att => {
            const st = attachmentState(att, uploadStates);
            return (
              <div key={att.id} style={S.fileRow}>
                <div style={S.fileIcon}>{FILE_ICONS[att.type] || "📎"}</div>
                <div style={S.fileMeta}>
                  <div style={S.fileName}>{att.name}</div>
                  <div className="quote-builder-file-sub" style={S.fileSub}>
                    <StateChip st={st} onRetry={() => onRetry(att.id)} inline />
                    <span>{fmtBytes(att.size)} · {fileExtLabel(att.name)}</span>
                    {att.aiRead && <span style={S.aiReadPill}>✓ AI read</span>}
                  </div>
                </div>
                <button type="button" title="Remove" onClick={() => onRemove(att.id)} style={S.rowDel}>×</button>
              </div>
            );
          })}
        </>
      )}

      {rejectedFiles.map(r => (
        <div key={r.id} style={{ ...S.fileRow, ...S.fileRowBad }}>
          <div style={{ ...S.fileIcon, ...S.fileIconBad }}>⚠️</div>
          <div style={S.fileMeta}>
            <div style={S.fileName}>{r.name}</div>
            <div className="quote-builder-file-sub" style={{ ...S.fileSub, ...S.fileSubBad }}>
              Too large to store (max {Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024))} MB) · {fmtBytes(r.size)} — not attached
            </div>
          </div>
          <button type="button" title="Dismiss" onClick={() => onDismissRejected(r.id)} style={S.rowDel}>×</button>
        </div>
      ))}

      {attachments.length === 0 && rejectedFiles.length === 0 && (
        <div style={S.attEmpty}>No attachments yet.</div>
      )}
    </div>
  );
}

// Compact storage-state indicator used on image cards + file rows.
function StateChip({ st, onRetry, inline }) {
  if (st.kind === "stored") return <span style={{ ...S.chipBase, color: C.green }}>{inline ? "Stored" : "✓ Stored"}</span>;
  if (st.kind === "uploading") return <span style={{ ...S.chipBase, color: C.blue }}>↑ Uploading…</span>;
  if (st.kind === "local") return <span style={{ ...S.chipBase, color: C.muted }}>Saved locally</span>;
  if (st.kind === "none") return <span style={{ ...S.chipBase, color: C.amber }}>Not stored</span>;
  if (st.kind === "error") {
    return (
      <span style={{ ...S.chipBase, color: C.danger, display: "inline-flex", alignItems: "center", gap: 6 }}>
        ⚠ Upload failed
        {onRetry && <button type="button" onClick={onRetry} style={S.retryBtn}>Retry</button>}
      </span>
    );
  }
  return null;
}

// Totals docked at the bottom of the Line Items panel (right-aligned), so the
// money sits with the line items it sums.
function TotalsDock({ totals, proBono = false, onToggleProBono, proBonoMismatch = false }) {
  return (
    <div style={S.dock}>
      <div style={S.dockIn}>
        {/* Pro bono switch. Line rates are never zeroed: the subtotal below keeps
            showing the value given, and the Pro bono discount row above takes the
            total to $0. Internal control; nothing here prints on the PDF. */}
        <div style={S.proBonoRow}>
          <button
            type="button"
            role="switch"
            aria-checked={proBono}
            aria-label="Pro bono"
            data-testid="qb-pro-bono-switch"
            style={{ ...S.switchTrack, ...(proBono ? S.switchTrackOn : {}) }}
            onClick={onToggleProBono}
          >
            <span style={{ ...S.switchKnob, ...(proBono ? S.switchKnobOn : {}) }} />
          </button>
          <div>
            <div style={S.switchTitle}>Pro bono</div>
            <div style={S.switchCap}>
              {proBono
                ? "Nothing is charged. Line rates stay on the document and a 100% Pro bono discount takes the total to $0."
                : "Provide this work at no charge. Adds a 100% Pro bono quote discount."}
            </div>
            {proBonoMismatch && (
              <div data-testid="qb-pro-bono-mismatch" style={S.proBonoWarn}>
                {PRO_BONO_MISMATCH_TEXT} The Pro bono discount was removed or edited. Add a 100% quote discount named Pro bono, or turn Pro bono off.
              </div>
            )}
          </div>
        </div>
        <div style={S.totalLine}><span>Subtotal</span><strong>{fmtMoney(totals.grossSubtotal)}</strong></div>
        <div style={S.totalLine}><span>Discounts</span><strong style={{ color: totals.totalSavings > 0 ? C.green : C.muted }}>{fmtMoney(-Math.round((totals.grossSubtotal - totals.total) * 100) / 100)}</strong></div>
        <div style={S.grandBlock}>
          <span style={S.grandLabel}>Quote Total</span>
          <span style={S.grandTotal}>{fmtMoney(totals.total)}</span>
        </div>
      </div>
    </div>
  );
}

function CatalogBrowser({ onPick }) {
  return (
    <div style={S.catalog}>
      {CATALOG_GROUPS.map(([title, items]) => (
        <div key={title}>
          <div style={{ ...lbl, marginBottom: 8 }}>{title}</div>
          <div style={S.catalogItems}>
            {items.map(item => (
              <button key={item.id} type="button" style={S.catalogItem} onClick={() => onPick(item)}>
                <span>{item.label}</span>
                {item.rate > 0 && <span style={S.catalogRate}>{fmtMoney(item.rate)}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Group picker for one line. Commits on blur or Enter, not per keystroke: a line whose
// group changes moves under another heading in the list, and a row that moves mid-word
// would lose focus. The datalist offers the groups already on the quote; typing a new
// name creates one.
function GroupInput({ idx, value, onCommit }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => { if (v.trim() !== (value || "")) onCommit(v.trim()); };
  return (
    <div style={S.groupPick}>
      <span style={S.groupPickLabel}>Group</span>
      <input aria-label={`Line item ${idx + 1} group`} list="qb-line-groups" style={S.groupInput} value={v} placeholder="None"
        onChange={(e) => setV(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} />
    </div>
  );
}

// Custom printed wording (docs/quotes/ai-box-plan.md): the sentences this quote prints in
// place of the tool's own. Read only here, written through the AI box, so it's never hidden.
function CustomWordingList({ quote, onReset }) {
  const rows = PRINT_WORDING_KEYS.map((key) => ({ key, text: wordingOf(quote, key) })).filter((r) => r.text !== null);
  if (!rows.length) return null;
  return (
    <div data-testid="custom-wording" style={S.customWording}>
      <div style={S.customWordingHead}>Custom printed wording</div>
      {rows.map((r) => (
        <div key={r.key} style={S.customWordingRow}>
          <span style={S.customWordingLabel}>{PRINT_WORDING_LABELS[r.key]}</span>
          <span style={S.customWordingText}>{r.text === "" ? "Removed, so it doesn't print." : r.text}</span>
          <button type="button" data-testid={`custom-wording-reset-${r.key}`} style={S.groupPrintsAsReset} onClick={() => onReset(r.key)}>Reset</button>
        </div>
      ))}
      {printWordingWarnings(quote).map((w, i) => <div key={i} data-testid="custom-wording-warning" style={S.customWordingWarn}>{w}</div>)}
    </div>
  );
}

// The heading note on a block row. Commits on blur or Enter for the same reason as
// GroupInput: the committed value is cleaned (trimmed), and a per-keystroke write would
// eat the space the operator just typed.
function HeadingNoteInput({ label, value, onCommit }) {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  const commit = () => { if (v.trim() !== (value || "")) onCommit(v); };
  return (
    <input aria-label={`${label} heading note`} style={S.groupNoteInput} value={v} placeholder="Heading note, for example the date or the place"
      onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }} />
  );
}

// New block (2026-09-04): the inline creator row under the list. Name required and unique
// on the quote (case-insensitive, so "pile" does not sit next to "Pile"); note optional;
// Enter creates, Escape cancels. Chips: the brief's blocks not yet on the quote (name +
// the brief's note) and the scheduled days (name = the day, note = the place).
function BlockCreator({ existing, brief, schedule, onCreate, onCancel }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);
  const label = cleanGroupLabel(name);
  const taken = !!label && existing.some((l) => l.toLowerCase() === label.toLowerCase());
  const canCreate = !!label && !taken;
  const create = () => { if (canCreate) onCreate(label, cleanGroupNote(note)); };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); create(); }
    else if (e.key === "Escape") { e.preventDefault(); onCancel(); }
  };
  const pick = (sg) => { setName(sg.label); setNote(sg.note || ""); nameRef.current?.focus(); };
  const chips = (title, list, withNote) => list.length > 0 && (
    <div style={S.suggRow}>
      <span style={S.suggKey}>{title}</span>
      {list.map((sg) => (
        <button key={`${sg.label}|${sg.note}`} type="button" style={{ ...S.chip, ...(sg.label === label ? S.chipOn : {}) }} onClick={() => pick(sg)}>
          {sg.label}{withNote && sg.note ? ` · ${sg.note}` : ""}
        </button>
      ))}
    </div>
  );
  return (
    <div style={S.blockCreator} data-testid="qb-block-creator">
      <div style={S.blockGrid}>
        <div style={S.blockField}>
          <label style={lbl} htmlFor="qb-block-name">Block name</label>
          <input id="qb-block-name" ref={nameRef} data-testid="qb-block-name" type="text" maxLength={60} style={S.blockInput} value={name}
            placeholder="For example Pile, or a capture day" onChange={(e) => setName(e.target.value)} onKeyDown={onKey} />
        </div>
        <div style={S.blockField}>
          <label style={lbl} htmlFor="qb-block-note">Heading note</label>
          <input id="qb-block-note" data-testid="qb-block-note" type="text" maxLength={120} style={S.blockInput} value={note}
            placeholder="Optional line under the heading, for example the date or the place." onChange={(e) => setNote(e.target.value)} onKeyDown={onKey} />
        </div>
      </div>
      {(brief.length > 0 || schedule.length > 0) && (
        <div style={S.sugg}>
          {chips("From the brief", brief, false)}
          {chips("From the schedule", schedule, true)}
        </div>
      )}
      <div style={S.blockFoot}>
        <span style={S.blockHint}>{taken ? `A block named ${label} is already on this quote.` : "Name required and unique on this quote. Note optional. Enter creates."}</span>
        <button type="button" style={btnS} onClick={onCancel}>Cancel</button>
        <button type="button" data-testid="qb-create-block" disabled={!canCreate}
          style={{ ...btnP, opacity: canCreate ? 1 : 0.4, cursor: canCreate ? "pointer" : "not-allowed" }} onClick={create}>Create block</button>
      </div>
    </div>
  );
}

function LineItemsTable({
  items,
  discounts,
  lineGroups,
  onGroupNoteUpdate,
  onGroupPrintLabelClear,
  onRemoveGroup,
  onUpdate,
  onRemove,
  onToggleDiscount,
  onDiscountUpdate,
  onAddQuoteDiscount,
  onQuoteDiscountUpdate,
  onQuoteDiscountRemove,
}) {
  // Grouped view (2026-09-03): lines print under their block heading with a subtotal, in
  // the order of the quote's block list; a block with no lines shows its empty state and
  // no subtotal; ungrouped lines last. `idx` stays the line's position in the quote so
  // every edit callback still hits the right line. No blocks = the flat list, as before.
  const blocks = Array.isArray(lineGroups) ? lineGroups : [];
  const order = blocks.map((g) => g.label);
  const groups = groupLineItems(items, lineGroupNotesFrom(blocks), order);
  const byLabel = new Map(groups.filter((g) => g.label).map((g) => [g.label, g]));
  // A group on a line that the list does not carry (the builder appends these, so only a
  // caller handing over a stale list gets here) still renders as a heading, after the list.
  const headings = [...blocks, ...groups.filter((g) => g.label && !order.includes(g.label)).map((g) => ({ label: g.label, note: g.note }))];
  const rest = groups.find((g) => !g.label);
  const grouped = headings.length > 0;
  const labels = headings.map((g) => g.label);
  const renderRow = (item, idx) => {
        const lineDisc = calcLineDiscount(item);
        const qty = item.qty != null && item.qty > 0 ? Number(item.qty) : 0;
        // Line total is GROSS (qty × rate) so the column sums to the Subtotal; the
        // discount is shown beneath and rolled into the Discounts row (Ivin 2026-08-19).
        const lineTotal = qty * (Number(item.rate) || 0);
        return (
          <div key={item.key || `${item.catalogId || "custom"}-${idx}`} className="quote-builder-line-row" style={S.lineRow}>
            <div>
              <input aria-label={`Line item ${idx + 1} description`} style={S.inlineInput} value={item.description || ""} onChange={e => onUpdate(idx, "description", e.target.value)} />
              <input aria-label={`Line item ${idx + 1} notes`} style={S.inlineSubInput} value={item.notes || ""} onChange={e => onUpdate(idx, "notes", e.target.value)} placeholder="Notes" />
              <GroupInput idx={idx} value={item.group || ""} onCommit={(g) => onUpdate(idx, "group", g)} />
            </div>
            <input aria-label={`Line item ${idx + 1} quantity`} type="number" min="0" style={S.numberInput} value={item.qty ?? ""} onChange={e => onUpdate(idx, "qty", e.target.value === "" ? null : Number(e.target.value) || 0)} />
            <input aria-label={`Line item ${idx + 1} rate`} type="number" min="0" style={S.numberInput} value={item.rate || 0} onChange={e => onUpdate(idx, "rate", Number(e.target.value) || 0)} />
            <div className="quote-builder-line-discount">
              {item.discount ? (
                <div style={S.discountControls}>
                  <input style={S.discountInput} value={item.discount.value || 0} onChange={e => onDiscountUpdate(idx, "value", Number(e.target.value) || 0)} />
                  <button type="button" style={S.discountToggle} onClick={() => onDiscountUpdate(idx, "type", item.discount.type === "%" ? "$" : "%")}>{item.discount.type}</button>
                  <button type="button" style={S.iconBtn} onClick={() => onToggleDiscount(idx)}>x</button>
                </div>
              ) : (
                <button type="button" style={S.linkBtn} onClick={() => onToggleDiscount(idx)}>Add</button>
              )}
              {(item.catalogId === "head_scan" || item.catalogId === "body_scan") && item.qty > 0 && (
                <div style={S.tier}>Tier {getTierLabel(item.catalogId, item.qty)}</div>
              )}
            </div>
            <div style={S.lineTotal}>
              {qty > 0 ? fmtMoney(lineTotal) : "n/a"}
              {qty > 0 && lineDisc > 0 && <div style={S.lineSaves}>{fmtMoney(-lineDisc)}</div>}
            </div>
            <button type="button" style={S.iconBtn} onClick={() => onRemove(idx)}>x</button>
          </div>
        );
  };
  return (
    <div style={S.lineBox}>
      <div className="quote-builder-line-head" style={S.lineHead}>
        <div>Description</div>
        <div>Qty</div>
        <div>Rate</div>
        <div>Discount</div>
        <div>Total</div>
        <div />
      </div>
      <datalist id="qb-line-groups">{labels.map((l) => <option key={l} value={l} />)}</datalist>
      {items.length === 0 && !grouped && <div style={S.noItems}>No line items yet.</div>}
      {!grouped
        ? items.map((item, idx) => renderRow(item, idx))
        : (
          <>
            {headings.map((g, gi) => {
              const found = byLabel.get(g.label);
              return (
                <React.Fragment key={g.label}>
                  <div className="quote-builder-group-row" style={S.groupRow}>
                    <span style={S.groupLabel}>{g.label}</span>
                    {g.printLabel && <span data-testid={`qb-block-prints-as-${gi}`} style={S.groupPrintsAs}>Prints as {g.printLabel} <button type="button" aria-label={`Print block ${g.label} under its own name`} style={S.groupPrintsAsReset} onClick={() => onGroupPrintLabelClear(g.label)}>Reset</button></span>}
                    <HeadingNoteInput label={g.label} value={g.note || ""} onCommit={(n) => onGroupNoteUpdate(g.label, n)} />
                    <button type="button" aria-label={`Remove block ${g.label}`} data-testid={`qb-remove-block-${gi}`} style={S.groupRemove} onClick={() => onRemoveGroup(g.label)}>x</button>
                  </div>
                  {found
                    ? found.items.map(({ item, idx }) => renderRow(item, idx))
                    : <div className="quote-builder-group-empty" style={S.groupEmpty}>No lines yet. Add from Catalog, LiDAR, or Custom, or move a line here.</div>}
                  {found && (
                    <div className="quote-builder-group-subtotal" style={S.subRow}><span>{g.label} subtotal</span><span style={S.subAmt}>{fmtMoney(found.subtotal)}</span></div>
                  )}
                </React.Fragment>
              );
            })}
            {rest && (
              <React.Fragment key="__ungrouped">
                <div className="quote-builder-group-row" data-testid="qb-no-group-row" style={S.groupRow}><span style={{ ...S.groupLabel, color: C.muted, fontWeight: 300 }}>No group (prints last, without a heading)</span></div>
                {rest.items.map(({ item, idx }) => renderRow(item, idx))}
              </React.Fragment>
            )}
          </>
        )}
      {(() => { const shown = quoteDiscountShownAmounts(items, discounts); return discounts.map((disc, idx) => {
        const amount = shown[idx]; // to the cent, adding up with the total (quoteDiscountShownAmounts)
        return (
          <div key={disc.key || `quote-discount-${idx}`} className="quote-builder-discount-row" style={S.quoteDiscountRow}>
            <input style={S.inlineInput} value={disc.name || ""} onChange={e => onQuoteDiscountUpdate(idx, "name", e.target.value)} placeholder="Quote discount" />
            <div />
            <div />
            <div style={S.discountControls}>
              <input style={S.discountInput} value={disc.value || 0} onChange={e => onQuoteDiscountUpdate(idx, "value", Number(e.target.value) || 0)} />
              <button type="button" style={S.discountToggle} onClick={() => onQuoteDiscountUpdate(idx, "type", disc.type === "%" ? "$" : "%")}>{disc.type}</button>
            </div>
            <div style={{ ...S.lineTotal, color: C.green }}>{fmtMoney(-amount)}</div>
            <button type="button" style={S.iconBtn} onClick={() => onQuoteDiscountRemove(idx)}>x</button>
          </div>
        );
      }); })()}
      <div className="quote-builder-line-foot" style={S.lineFoot}>
        <button type="button" style={S.linkBtn} onClick={onAddQuoteDiscount}>+ Add quote discount</button>
      </div>
    </div>
  );
}


const S = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  kicker: {
    fontSize: 9.5,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.16em",
    marginBottom: 5,
  },
  title: {
    fontSize: 20,
    color: C.text,
    fontWeight: 400,
  },
  headerActions: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 4,
    paddingTop: 18,
    borderTop: `1px solid ${C.border}`,
  },
  footerHint: {
    marginRight: "auto",
    fontSize: 12,
    color: C.muted,
  },
  notice: {
    background: `${C.green}12`,
    border: `1px solid ${C.green}33`,
    color: C.text,
    borderRadius: 6,
    padding: "9px 12px",
    fontSize: 12,
    marginBottom: 14,
  },
  unsavedNote: {
    background: `${C.amber}12`,
    border: `1px solid ${C.amber}55`,
    color: C.text,
    borderRadius: 6,
    padding: "9px 12px",
    fontSize: 12,
    marginBottom: 14,
  },
  // The lock (quotePersistence.js): the form can't be clicked and the message floats above it.
  lockedForm: { pointerEvents: "none", opacity: 0.6 },
  busyOverlay: { position: "absolute", inset: 0, zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 80, pointerEvents: "all" },
  busyCard: { position: "sticky", top: 80, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 18px", fontSize: 14, color: C.text, boxShadow: "0 8px 30px rgba(0,0,0,0.12)", maxWidth: 520 },
  busyStuck: { marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: C.danger },
  main: {
    display: "grid",
    gap: 14,
    minWidth: 0,
  },
  panel: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "20px 22px",
  },
  panelHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  panelTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    marginBottom: 16,
  },
  panelAccent: {
    width: 3,
    height: 13,
    borderRadius: 2,
    background: C.text,
    flexShrink: 0,
  },
  panelTitle: {
    fontSize: 12,
    color: C.text,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 12,
    marginBottom: 12,
  },
  locationRow: {
    display: "grid",
    gridTemplateColumns: "auto minmax(0, 1fr)",
    gap: 12,
    alignItems: "end",
    marginBottom: 12,
  },
  checkLabel: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    minHeight: 39,
    color: C.text,
    fontSize: 12,
  },
  field: {
    ...inp,
    background: C.surface,
    fontWeight: 400,
  },
  textarea: {
    ...inp,
    background: C.surface,
    resize: "vertical",
    lineHeight: 1.5,
    fontWeight: 400,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    justifyContent: "flex-end",
  },
  // Blocks (2026-09-04): the "Adding to" pill beside the panel title (a native select
  // dressed as the pill), the open state of the New block button, the creator row (the
  // estimator's field styles), its suggestion chips, and the empty-block line.
  panelLeft: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14, minWidth: 0 },
  addingTo: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px 4px 11px", marginBottom: 16,
    borderRadius: 999, border: `1px solid ${C.border}`, background: C.surfaceAlt, fontSize: 11.5, color: C.muted, whiteSpace: "nowrap", fontFamily: font,
  },
  addingToSelect: {
    ...sel, width: "auto", padding: "0 16px 0 0", border: "none", backgroundColor: "transparent", backgroundPosition: "right 2px center",
    fontSize: 11.5, fontWeight: 500, color: C.text,
  },
  btnOpen: { background: C.surfaceAlt, color: C.text },
  blockCreator: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, display: "grid", gap: 12, marginTop: 12 },
  blockGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },
  blockField: { display: "grid", gap: 4 },
  blockInput: { ...inp, background: C.surface, fontWeight: 400 },
  sugg: { display: "grid", gap: 8 },
  suggRow: { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
  suggKey: { fontFamily: mono, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase", color: C.muted, width: 128, flexShrink: 0 },
  chip: { ...btnS, display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", fontSize: 11.5, background: C.surface },
  chipOn: { background: C.text, color: "#FFF", borderColor: C.text },
  // Holds (2026-09-04): the block under the details grid; amber conflict lines and
  // the quiet grey all-clear line; chip rows; the switch row.
  holdBlock: { display: "grid", gap: 12, marginBottom: 12 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  holdWarn: {
    display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: C.capture,
    background: `${C.amber}14`, border: `1px solid ${C.amber}40`, borderRadius: 6, padding: "8px 10px", lineHeight: 1.45,
  },
  holdOk: {
    display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: C.muted,
    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", lineHeight: 1.45,
  },
  holdBang: {
    width: 14, height: 14, borderRadius: 7, background: C.amber, color: C.surface, fontSize: 9, fontWeight: 500,
    display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none", fontFamily: mono, lineHeight: 1, marginTop: 1,
  },
  holdBangOk: { background: C.border, color: C.muted },
  holdHint: { fontSize: 12, color: C.muted },
  switchRow: { display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 12px", border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface },
  switchTrack: { flex: "none", marginTop: 2, width: 30, height: 17, borderRadius: 9, background: C.border, border: "none", padding: 0, position: "relative", cursor: "pointer" },
  switchTrackOn: { background: C.green },
  switchKnob: { position: "absolute", top: 2, left: 2, width: 13, height: 13, borderRadius: 7, background: C.surface, boxShadow: "0 1px 2px rgba(0,0,0,0.2)", transition: "left 0.12s ease" },
  switchKnobOn: { left: 15 },
  switchTitle: { fontSize: 13, color: C.text, fontWeight: 400 },
  switchCap: { fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.45 },
  blockFoot: { display: "flex", alignItems: "center", gap: 8 },
  blockHint: { flex: 1, fontSize: 11.5, color: C.muted },
  groupRemove: { border: "none", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 13, padding: "4px 8px", marginLeft: "auto", fontFamily: font },
  groupPrintsAs: { fontSize: 11.5, color: C.muted, whiteSpace: "nowrap" },
  groupPrintsAsReset: { border: "none", background: "transparent", color: C.muted, cursor: "pointer", fontSize: 11.5, padding: "0 4px", textDecoration: "underline", fontFamily: font },
  customWording: { marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` },
  customWordingHead: { fontSize: 12.5, fontWeight: 500, color: C.text, marginBottom: 4 },
  customWordingRow: { display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: C.text, padding: "3px 0" },
  customWordingLabel: { color: C.muted, whiteSpace: "nowrap" },
  customWordingText: { flex: 1, minWidth: 0 },
  customWordingWarn: { fontSize: 12.5, lineHeight: 1.5, color: C.text, border: `1px solid ${C.amber}`, borderRadius: 6, padding: "6px 10px", marginTop: 6 },
  groupEmpty: { padding: "10px 10px 11px 28px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}` },
  catalog: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  catalogItems: {
    display: "grid",
    gap: 4,
  },
  catalogItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    border: `1px solid ${C.border}`,
    background: C.surface,
    color: C.text,
    borderRadius: 5,
    padding: "6px 8px",
    cursor: "pointer",
    fontFamily: font,
    fontSize: 11,
    textAlign: "left",
  },
  catalogRate: {
    color: C.muted,
    fontFamily: mono,
    whiteSpace: "nowrap",
  },
  lidarBanner: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", marginBottom: 12, background: "#FBF6EA", border: `1px solid ${C.amber}`, borderRadius: 8, fontSize: 13 },
  lidarProg: { display: "inline-flex", gap: 4, marginLeft: 10, verticalAlign: "middle" },
  lidarDot: { width: 18, height: 6, borderRadius: 3, background: "#E9DFC6", display: "block" },
  lidarDotOn: { background: C.amber },
  lineBox: {
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  // Line groups (2026-09-03): a computed heading row per group, a subtotal row under it,
  // and a small group picker under each line's notes.
  groupRow: {
    display: "flex", alignItems: "center", gap: 12, padding: "7px 10px",
    background: "#F7F5F1", borderBottom: `1px solid ${C.border}`,
  },
  groupLabel: { fontSize: 12.5, fontWeight: 500, color: C.text, whiteSpace: "nowrap" },
  groupNoteInput: {
    ...inp, flex: 1, padding: "4px 8px", fontSize: 11, background: "transparent", border: "1px solid transparent", color: C.muted, maxWidth: 420,
  },
  subRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 42px 6px 10px",
    background: "#FCFBF9", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted,
  },
  subAmt: { fontFamily: mono, color: C.text, fontSize: 12.5 },
  groupPick: { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4, marginLeft: 2 },
  groupPickLabel: { fontSize: 10.5, color: C.muted },
  groupInput: { ...inp, width: 150, padding: "2px 6px", fontSize: 11, background: C.surfaceAlt },
  lineHead: {
    display: "grid",
    gridTemplateColumns: "minmax(190px, 1fr) 72px 86px 100px 96px 32px",
    gap: 8,
    background: "#FAFAF8",
    borderBottom: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: 0,
    padding: "9px 10px",
  },
  noItems: {
    padding: 24,
    color: C.muted,
    fontSize: 12,
    textAlign: "center",
  },
  lineRow: {
    display: "grid",
    gridTemplateColumns: "minmax(190px, 1fr) 72px 86px 100px 96px 32px",
    gap: 8,
    alignItems: "center",
    padding: "8px 10px",
    borderBottom: `1px solid ${C.border}`,
  },
  quoteDiscountRow: {
    display: "grid",
    gridTemplateColumns: "minmax(190px, 1fr) 72px 86px 100px 96px 32px",
    gap: 8,
    alignItems: "center",
    padding: "8px 10px",
    borderBottom: `1px solid ${C.border}`,
    background: `${C.green}08`,
  },
  inlineInput: {
    ...inp,
    padding: "6px 8px",
    fontSize: 12,
    background: C.surface,
  },
  inlineSubInput: {
    ...inp,
    padding: "4px 8px",
    fontSize: 10.5,
    background: C.surface,
    color: C.muted,
    marginTop: 4,
  },
  numberInput: {
    ...inp,
    padding: "6px 8px",
    fontSize: 12,
    textAlign: "right",
    fontFamily: mono,
    background: C.surface,
  },
  discountControls: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  discountInput: {
    ...inp,
    padding: "5px 6px",
    width: 44,
    fontSize: 11,
    color: C.green,
    fontFamily: mono,
    background: C.surface,
  },
  discountToggle: {
    border: `1px solid ${C.green}40`,
    background: `${C.green}10`,
    color: C.green,
    borderRadius: 4,
    padding: "4px 6px",
    cursor: "pointer",
    fontFamily: mono,
    fontSize: 10,
  },
  iconBtn: {
    border: "none",
    background: "transparent",
    color: C.muted,
    cursor: "pointer",
    fontSize: 13,
    padding: 4,
  },
  linkBtn: {
    border: "none",
    background: "transparent",
    color: C.blue,
    cursor: "pointer",
    fontFamily: font,
    fontSize: 11,
    padding: 0,
  },
  tier: {
    color: C.muted,
    fontSize: 9.5,
    marginTop: 4,
  },
  lineSaves: { fontSize: 11, color: C.green, fontWeight: 400, marginTop: 2 },
  lineTotal: {
    fontFamily: mono,
    fontSize: 12,
    color: C.text,
    textAlign: "right",
  },
  lineFoot: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    background: "#FAFAF8",
    padding: "10px 12px",
  },
  footTotals: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
    justifyContent: "flex-end",
    color: C.muted,
    fontSize: 12,
  },
  formatGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  formatChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    padding: "5px 8px",
    color: C.text,
    fontSize: 11,
    cursor: "pointer",
  },
  pricingToggle: {
    display: "flex",
    alignItems: "flex-start",
    gap: 9,
    background: "#FAFAF8",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "11px 13px",
  },
  pricingHint: {
    fontSize: 10.5,
    color: C.muted,
    marginTop: 3,
    lineHeight: 1.5,
  },
  autoPill: {
    display: "inline-block",
    flexShrink: 0,
    fontSize: 8.5,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    background: C.green,
    color: "#FFFFFF",
    borderRadius: 4,
    padding: "2px 6px",
    marginRight: 6,
    fontWeight: 500,
  },
  sectionsHelp: {
    fontSize: 11,
    color: C.muted,
    marginTop: -6,
    marginBottom: 6,
    lineHeight: 1.5,
  },
  // ── Attachments panel ──
  attHelp: {
    fontSize: 11,
    color: C.muted,
    marginTop: -8,
    marginBottom: 14,
    lineHeight: 1.5,
  },
  cmHelp: {
    fontSize: 11,
    color: C.muted,
    marginTop: -8,
    marginBottom: 14,
    lineHeight: 1.5,
  },
  cmCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  cmCardPending: {
    borderColor: `${C.amber}59`,
  },
  cmHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "11px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  cmHeadPending: {
    background: `${C.amber}10`,
  },
  cmHeadL: { display: "flex", alignItems: "center", gap: 8 },
  cmHeadLab: { fontSize: 12, fontWeight: 400, color: C.text },
  cmHeadSub: { fontSize: 10.5, color: C.amber },
  cmLinkAll: {
    background: "transparent",
    color: C.green,
    border: `1px solid ${C.green}59`,
    borderRadius: 6,
    padding: "5px 11px",
    fontSize: 10.5,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: font,
  },
  cmRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  cmWho: { display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0 },
  cmDot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  cmName: { fontSize: 13, fontWeight: 400, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cmDetail: { fontSize: 11, color: C.muted, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  cmVerdict: { fontSize: 11, color: C.muted, textAlign: "right", minWidth: 120, flexShrink: 0 },
  cmActs: { display: "flex", gap: 6, flexShrink: 0 },
  cmBtn: {
    background: "transparent",
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: font,
  },
  cmBtnLink: { color: C.green, borderColor: `${C.green}59` },
  cmUndo: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "5px 8px",
    fontSize: 10.5,
    fontFamily: font,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  cmUndoHint: { fontSize: 10, color: C.amber, opacity: 0.65 },
  aiReadPill: { fontSize: 9.5, color: C.blue, background: `${C.blue}12`, borderRadius: 4, padding: "2px 6px", letterSpacing: "0.04em", whiteSpace: "nowrap" },
  dropzone: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    border: `1.5px dashed ${C.border}`,
    borderRadius: 8,
    background: C.bg,
    padding: 18,
    fontSize: 12.5,
    color: C.muted,
    cursor: "pointer",
    marginBottom: 16,
    transition: "border-color .15s ease, background .15s ease",
  },
  dropzoneOver: {
    borderColor: C.blue,
    background: `${C.blue}08`,
  },
  dropAcc: {
    fontSize: 10.5,
    color: C.muted,
  },
  attSubhead: {
    fontSize: 10,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    color: C.muted,
    margin: "6px 0 10px",
  },
  imgGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 18,
  },
  imgCard: {
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "hidden",
    background: C.surface,
  },
  thumb: {
    height: 148,
    background: "#E8E8E8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9A9A9A",
    fontSize: 11,
    position: "relative",
  },
  badge: {
    position: "absolute",
    top: 8,
    left: 8,
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "rgba(26,26,26,0.82)",
    color: "#FFF",
    fontSize: 11,
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  thumbDel: {
    position: "absolute",
    top: 7,
    right: 7,
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "none",
    background: "rgba(26,26,26,0.6)",
    color: "#FFF",
    fontSize: 14,
    lineHeight: 1,
    cursor: "pointer",
  },
  cardBody: {
    padding: "9px 11px 11px",
  },
  capInput: {
    ...inp,
    padding: "7px 10px",
    fontSize: 12,
    marginBottom: 9,
  },
  cardFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  inclLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.5,
    color: C.text,
    cursor: "pointer",
  },
  chipBase: {
    fontSize: 10.5,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  retryBtn: {
    border: `1px solid ${C.danger}55`,
    background: "transparent",
    color: C.danger,
    borderRadius: 4,
    padding: "1px 6px",
    fontSize: 10,
    cursor: "pointer",
    fontFamily: font,
  },
  prog: {
    height: 3,
    borderRadius: 2,
    background: C.border,
    overflow: "hidden",
    marginTop: 8,
  },
  progBar: {
    display: "block",
    height: "100%",
    width: "60%",
    background: C.blue,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 12px",
    marginBottom: 8,
    background: C.surface,
  },
  fileRowBad: {
    borderColor: `${C.danger}66`,
    background: `${C.danger}0A`,
  },
  fileIcon: {
    width: 30,
    height: 30,
    borderRadius: 6,
    background: C.surfaceAlt,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
  },
  fileIconBad: {
    background: `${C.danger}1A`,
  },
  fileMeta: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileSub: {
    fontSize: 11,
    color: C.muted,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },
  fileSubBad: {
    color: C.danger,
    fontWeight: 500,
  },
  rowDel: {
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.muted,
    borderRadius: 6,
    width: 26,
    height: 26,
    cursor: "pointer",
    fontSize: 14,
    lineHeight: 1,
    flexShrink: 0,
  },
  attEmpty: {
    padding: "14px 0 4px",
    color: C.muted,
    fontSize: 12,
    textAlign: "center",
  },
  sectionRow: {
    padding: "11px 0",
    borderTop: `1px solid ${C.border}`,
  },
  sectionRowFirst: {
    borderTop: "none",
  },
  sectionHeadRow: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    cursor: "pointer",
  },
  sectionName: {
    fontSize: 13,
    fontWeight: 500,
    color: C.text,
  },
  sectionNameOff: {
    fontSize: 13,
    fontWeight: 400,
    color: C.muted,
  },
  badgeOn: {
    fontSize: 9,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 500,
    background: "#F0EEEA",
    color: C.muted,
  },
  badgeDep: {
    fontSize: 9,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 500,
    background: `${C.amber}1F`,
    color: "#9A6A08",
  },
  badgeStudio: {
    fontSize: 9,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 500,
    background: `${C.blue}1A`,
    color: "#2B6BD1",
  },
  sectionText: {
    ...inp,
    background: C.surface,
    resize: "vertical",
    lineHeight: 1.55,
    fontWeight: 400,
    fontSize: 12,
    marginTop: 8,
    marginLeft: 24,
    width: "calc(100% - 24px)",
  },
  sessCard: {
    marginBottom: 12,
    padding: "14px 16px",
    background: `${C.blue}06`,
    border: `1px solid ${C.blue}22`,
    borderRadius: 8,
  },
  sessHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 12,
  },
  sessTitle: {
    fontSize: 11,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.blue,
  },
  sessSummary: {
    fontSize: 10.5,
    color: C.muted,
  },
  sessGrid: {
    display: "grid",
    gridTemplateColumns: "20px minmax(0, 1.1fr) minmax(0, 0.85fr) minmax(0, 1.4fr) 60px 24px",
    gap: 8,
    alignItems: "center",
  },
  sessGridHead: {
    fontSize: 9,
    color: C.muted,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    paddingBottom: 6,
    marginBottom: 4,
    borderBottom: `1px solid ${C.border}`,
  },
  sessRow: {
    padding: "6px 0",
  },
  sessIdx: {
    fontSize: 10,
    color: C.muted,
    textAlign: "right",
  },
  sessInput: {
    ...inp,
    padding: "6px 9px",
    fontSize: 12,
    background: C.surface,
  },
  studioBtn: (on) => ({
    padding: "6px 8px",
    fontSize: 10,
    border: `1px solid ${on ? C.blue + "55" : C.border}`,
    background: on ? `${C.blue}10` : "transparent",
    color: on ? C.blue : C.muted,
    borderRadius: 5,
    cursor: "pointer",
    fontFamily: font,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    width: "100%",
  }),
  sessRemove: {
    background: "transparent",
    border: "none",
    color: C.muted,
    cursor: "pointer",
    fontSize: 16,
    padding: 0,
  },
  sessError: {
    marginTop: 8,
    padding: "6px 10px",
    background: `${C.danger}0F`,
    border: `1px solid ${C.danger}33`,
    borderRadius: 4,
    color: C.danger,
    fontSize: 11,
  },
  addMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 4,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 5,
    padding: 4,
    minWidth: 150,
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    zIndex: 10,
  },
  addItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 10px",
    fontSize: 12,
    cursor: "pointer",
    borderRadius: 4,
    color: C.text,
  },
  sessTravelNote: {
    marginTop: 10,
    fontSize: 10.5,
    color: C.muted,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  dock: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 18,
    paddingTop: 16,
    borderTop: `1px solid ${C.border}`,
  },
  dockIn: {
    width: 300,
    maxWidth: "100%",
  },
  proBonoRow: {
    display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 12px", marginBottom: 14,
    border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface,
  },
  proBonoWarn: {
    marginTop: 6, fontSize: 12, lineHeight: 1.45, color: C.amber,
  },
  totalLine: {
    display: "flex",
    justifyContent: "space-between",
    color: C.muted,
    fontSize: 12,
    marginBottom: 9,
  },
  grandBlock: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: C.text,
    borderRadius: 8,
    padding: "13px 16px",
    marginTop: 6,
  },
  grandLabel: {
    fontSize: 10.5,
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "rgba(255,255,255,0.62)",
  },
  grandTotal: {
    fontFamily: mono,
    fontSize: 23,
    color: "#FFFFFF",
  },
};
